import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources with native, consistent translations
const resources = {
  // =====================
  // ENGLISH
  // =====================
  en: {
    translation: {
      // App Branding
      appName: "Schemes Portal",
      tagline: "Empowering Citizens",
      
      // Common Actions
      login: "Login",
      logout: "Logout",
      signup: "Sign Up",
      home: "Home",
      back: "Back",
      search: "Search",
      filters: "Filters",
      clear: "Clear",
      viewDetails: "View Details",
      applyNow: "Apply Now",
      saved: "Saved",
      all: "All",
      news: "News",
      loading: "Loading...",
      submit: "Submit",
      cancel: "Cancel",
      save: "Save",
      delete: "Delete",
      edit: "Edit",
      close: "Close",
      yes: "Yes",
      no: "No",
      ok: "OK",
      continue: "Continue",
      note: "Note",
      adminDashboard: "Admin Dashboard",
      welcome: "Welcome!",
      
      // Auth Prompt Modal
      loginRequired: "Login Required",
      loginToAccess: "Please login to access this feature",
      loginToExploreSchemes: "Login to explore government schemes and find benefits you deserve",
      loginToAccessNews: "Login to read latest government news and updates",
      loginToAccessAll: "Login to access all features",
      authPromptMessage: "Create an account or login to explore government schemes, save favorites, and get personalized recommendations.",
      guestNote: "You can browse the home page without an account",
      continueRequiresLogin: "Login to Continue",
      forcedAuthMessage: "You've been browsing for a while — please login or sign up (free) to continue exploring schemes and jobs.",
      
      // Login Page
      welcomeBack: "Welcome Back",
      loginToAccess: "Login to access government schemes",
      phoneNumber: "Phone Number",
      phonePlaceholder: "10-digit mobile number",
      password: "Password",
      passwordPlaceholder: "Enter your password",
      rememberMe: "Remember me",
      loggingIn: "Logging in...",
      noAccount: "Don't have an account?",
      forgotPassword: "Forgot Password?",
      
      // Signup Page
      createAccount: "Create Account",
      signupTagline: "Discover schemes tailored for you",
      fullName: "Full Name",
      fullNamePlaceholder: "Enter your full name",
      confirmPassword: "Confirm Password",
      confirmPasswordPlaceholder: "Re-enter your password",
      state: "State",
      selectState: "Select State",
      district: "District",
      selectDistrict: "Select District",
      age: "Age",
      agePlaceholder: "Enter your age",
      selectAge: "Select your age",
      casteCategory: "Caste Category",
      selectCategory: "Select Category",
      occupation: "Occupation",
      selectOccupation: "Select Occupation",
      specifyOccupation: "Specify your occupation",
      enterOccupation: "Enter occupation",
      gender: "Gender",
      selectGender: "Select Gender",
      genderMale: "Male",
      genderFemale: "Female",
      genderOther: "Other",
      householdIncome: "Household Income",
      selectIncome: "Select Income Range",
      creatingAccount: "Creating account...",
      alreadyHaveAccount: "Already have an account?",
      signupCaution: "Please enter accurate personal and income details. Incorrect information may affect your eligibility.",
      termsAgreementLabel: "I have read and agree to the",
      termsConditionsPrivacy: "Terms, Conditions, and Privacy Policy",
      termsNotAccepted: "You must accept the Terms, Conditions, and Privacy Policy to register",
      termsAcceptedConfirm: "You have agreed to the terms",
      termsModalTitle: "Terms, Conditions & Privacy Policy",
      acceptTerms: "I Accept the Terms",
      
      // Occupation Options
      occupationAgriculture: "Agriculture/Farming",
      occupationIT: "IT/Technology",
      occupationBanking: "Banking/Finance",
      occupationBusiness: "Business/Self-employed",
      occupationAccountant: "Accountant",
      occupationHomeMaker: "Homemaker",
      occupationGovernment: "Government Service",
      occupationHealthCare: "Healthcare",
      occupationCrafts: "Handicrafts/Artisan",
      occupationManualLabor: "Daily Wage Worker",
      occupationCreativeMedia: "Arts/Media",
      occupationStudent: "Student",
      occupationSalesRetail: "Sales/Retail",
      occupationTeachers: "Teacher/Professor",
      occupationOthers: "Others",
      
      // Home Page
      indiaLargestPortal: "India's Largest Welfare Schemes Portal",
      transformingLives: "Transforming Lives Across",
      incredibleIndia: "Incredible India",
      reachingHouseholds: "Reaching",
      householdsText: "30+ Crore households",
      acrossIndia: "across India with welfare schemes",
      discoverBenefits: "Discover benefits",
      youDeserve: "you deserve",
      findBenefitsNow: "Find Your Benefits",
      totalPopulation: "Total Population",
      nationalCoverage: "National Coverage",
      householdsReached: "Households Reached",
      directBenefitTransfers: "Direct Transfers",
      realPeopleRealImpact: "Real Impact",
      meetTheFaces: "Meet the beneficiaries",
      farmers: "Farmers",
      women: "Women",
      students: "Students",
      seniors: "Senior Citizens",
      everyMinuteCounts: "Every Minute Counts",
      transferredPerMin: "Transferred/min",
      familiesPerMin: "Families/min",
      support247: "24/7 Support",
      dontMissOut: "Don't Miss Your Benefits",
      joinMillions: "Join millions who discovered schemes that changed their lives",
      startExploringNow: "Start Exploring",
      exploreSchemes: "Schemes & Jobs",
      navImpact: "KMS Smart",
      loginForImpact: "Login to use KMS Smart",
      
      // Schemes Page
      discoverYourBenefits: "Discover Your Benefits",
      searchThrough: "Search through",
      activeSchemes: "active schemes",
      searchSchemes: "Search schemes...",
      sector: "Sector",
      selectSector: "Select Sector",
      category: "Category",
      type: "Type",
      selectType: "Select Type",
      found: "Found",
      schemes: "schemes",
      scheme: "scheme",
      freeSchemes: "Free Schemes",
      premiumSchemes: "Premium Schemes",
      locked: "Locked",
      unlocked: "Unlocked",
      loadingSchemes: "Loading schemes...",
      noSchemesFound: "No schemes found",
      tryAdjusting: "Try adjusting your filters",
      clearAllFilters: "Clear All Filters",
      getWhatYouDeserve: "Get What You Deserve!",
      dontMissOutOnBenefits: "Don't miss out on government benefits. We help you find and apply for schemes you're eligible for.",
      statesCovered: "States Covered",
      newSchemesAdded: "NEW SCHEMES ADDED!",
      farmerSchemes: "Farmer Schemes",
      educationScholarships: "Education & Scholarships",
      womenEmpowerment: "Women Empowerment",
      healthWelfare: "Health & Welfare",
      free: "Free",
      premium: "Premium",
      premiumScheme: "Premium Scheme",
      premiumSchemeAccessDesc: "This is a premium scheme. Subscribe to get expert assistance with your application.",
      viewsRemaining: "view(s) remaining",
      viewsExhausted: "Views Exhausted",
      viewsExhaustedDesc: "You have used all your views for this premium scheme. Contact our team for assistance with your application.",
      lastView: "Last View",
      contactTeam: "Contact Team",
      unlockNow: "Unlock Now",
      getAssistance: "Get Assistance",
      eligible: "eligible",
      youAreEligibleFor: "You are eligible for",
      
      // Scheme Details
      eligibilityCriteria: "Eligibility Criteria",
      benefits: "Benefits",
      requiredDocuments: "Required Documents",
      howToApply: "How to Apply",
      deadline: "Deadline",
      browseMoreSchemes: "Browse More Schemes",
      casteEligibility: "Caste Eligibility",
      schemeNotFound: "Scheme Not Found",
      schemeNotFoundDesc: "This scheme is not available",
      backToSchemes: "Back to Schemes",
      schemeDetails: "Scheme Details",
      
      // Saved Schemes
      yourSavedSchemes: "Your Saved Schemes",
      youHave: "You have",
      savedSchemes: "saved schemes",
      savedScheme: "saved scheme",
      noSavedSchemesYet: "No saved schemes yet",
      startExploring: "Start exploring and save schemes you like",
      browseAllSchemes: "Browse All Schemes",
      savedOn: "Saved on",
      removeFromSaved: "Remove",
      allSchemes: "All Schemes",
      
      // Notifications
      notifications: "Notifications",
      markAllRead: "Mark all read",
      noNotificationsYet: "No notifications yet",
      notifyAboutSchemes: "We'll notify you about new schemes",
      justNow: "Just now",
      minutesAgo: "{{count}}m ago",
      hoursAgo: "{{count}}h ago",
      daysAgo: "{{count}}d ago",
      
      // Premium/Subscription
      premiumAssistance: "Premium Assistance",
      premiumAssistanceRequired: "Premium Assistance Required",
      unlockPremium: "Unlock Premium",
      subscribeNow: "Subscribe Now",
      viewPlans: "View Plans",
      maybeLater: "Maybe Later",
      viewFreeSchemes: "View Free Schemes",
      teamWillAssist: "Team Will Assist",
      ourTeamWillContact: "Our team will contact you",
      unlockHighValueSchemes: "Unlock high-value schemes",
      expertAssistanceDesc: "Our experts help with eligibility verification, form filling, and complete application support.",
      eligibilityVerification: "Eligibility verification for all schemes",
      formFillingAssistance: "Form filling & document assistance",
      teamContactsAfterPayment: "Team contacts you after payment",
      startingFrom: "Starting from",
      perYear: "/year",
      assistanceNote: "We provide assistance only. Subscription does NOT guarantee scheme approval.",
      paymentSuccessful: "Payment Successful!",
      teamWillContactSoon: "Our team will reach out to you shortly to assist with your eligible schemes.",
      whatHappensNext: "What happens next:",
      viewsRemaining: "views remaining",
      viewsExhausted: "You have used all your views for this scheme",
      clickToView: "Click to view scheme details",
      contactTeam: "Contact Team",
      noViewsRemaining: "You have used all your views for this scheme. Please contact our team for assistance.",
      lastViewUsed: "This was your last view for this scheme.",
      keepPhoneAvailable: "Please keep your phone available for communication.",
      documentGuidance: "Document upload guidance",
      applicationSupport: "Application support",
      eligibilityCheck: "Eligibility Check",
      eligibilityCheckDesc: "We verify your eligibility for all applicable government schemes",
      formFilling: "Form Filling",
      formFillingDesc: "Expert assistance with application forms and document preparation",
      phoneSupport: "Phone Support",
      phoneSupportDesc: "Dedicated support team available to guide you through the process",
      getExpertHelp: "Get Expert Help with Your Applications",
      expertHelpDesc: "Our team of experts will guide you through the entire application process, from eligibility checking to document submission.",
      activeSubscription: "Active Subscription",
      yourPlanActive: "Your {{plan}} plan is active until {{date}}",
      bestValue: "Best Value",
      selectPlan: "Select Plan",
      alreadySubscribed: "Already Subscribed",
      importantInformation: "Important Information",
      importantInfoList1: "We provide assistance services only, NOT government benefits directly.",
      importantInfoList2: "Government authorities make final decisions on scheme approvals.",
      importantInfoList3: "Our team will contact you within 24-48 hours of payment.",
      importantInfoList4: "Subscription does NOT guarantee scheme approval.",
      termsAndConditions: "Terms & Conditions",
      importantDisclaimer: "Important Disclaimer",
      disclaimerText: "We do NOT sell government schemes or benefits. The subscription fee covers ONLY service assistance.",
      serviceIncludes: "Service Includes:",
      serviceItem1: "Eligibility checking assistance",
      serviceItem2: "Form filling support",
      serviceItem3: "Document upload guidance",
      serviceItem4: "Application process support",
      importantPoints: "Important Points:",
      importantPoint1: "Payment does NOT guarantee scheme approval",
      importantPoint2: "Only 150 free schemes are accessible to all users",
      importantPoint3: "Premium schemes remain locked even after payment - our team assists with applications",
      importantPoint4: "Full scheme list is accessible only to Admin",
      importantPoint5: "After payment, our team will contact you to assist",
      subscriptionOptions: "Subscription Options:",
      refundPolicy: "Refund Policy:",
      refundPolicyText: "No refunds after successful payment unless required by law.",
      agreeAndPay: "Agree & Pay",
      processing: "Processing...",
      
      // Filters
      central: "Central",
      stateType: "State",
      general: "General",
      sc: "SC",
      st: "ST",
      obc: "OBC",
      
      // Sector Names
      sectorAgriculture: "Agriculture",
      sectorHealth: "Health",
      sectorHousing: "Housing",
      sectorEducation: "Education",
      sectorEmployment: "Employment",
      sectorBusiness: "Business",
      sectorWomenChild: "Women & Child",
      sectorSocialWelfare: "Social Welfare",
      sectorFinancial: "Financial",
      sectorRural: "Rural Development",
      sectorUrban: "Urban Development",
      sectorSkill: "Skill Development",
      sectorSkillsEmployment: "Skills & Employment",
      sectorFood: "Food Security",
      sectorInsurance: "Insurance",
      sectorPension: "Pension",
      
      // States
      andhrapradesh: "Andhra Pradesh",
      telangana: "Telangana",
      karnataka: "Karnataka",
      tamilnadu: "Tamil Nadu",
      kerala: "Kerala",
      maharashtra: "Maharashtra",
      
      // Language
      language: "Language",
      english: "English",
      telugu: "తెలుగు",
      hindi: "हिंदी",
      kannada: "ಕನ್ನಡ",
      selectLanguage: "Select Language",
      
      // News Page
      latestNews: "Latest News",
      latestIndiaNews: "Latest India News",
      newsSubtitle: "Government schemes, policies & regional updates",
      newsUpdates: "News Updates",
      readMore: "Read More",
      lastUpdated: "Last updated",
      refreshNews: "Refresh News",
      searchNews: "Search news...",
      loadingNews: "Loading news...",
      noNewsFound: "No news found",
      tryDifferentSearch: "Try a different search term",
      newsWillBeUpdated: "News will be updated shortly",
      
      // Profile
      profile: "Profile",
      myProfile: "My Profile",
      editProfile: "Edit Profile",
      saveChanges: "Save Changes",
      deleteAccount: "Delete Account",
      personalInfo: "Personal Information",
      accountSettings: "Account Settings",
      
      // Errors & Messages
      error: "Error",
      success: "Success",
      somethingWentWrong: "Something went wrong",
      tryAgain: "Try Again",
      pleaseWait: "Please wait...",
      noDataFound: "No data found",
      connectionError: "Connection error",
      
      // Common UI
      back: "Back to",
      home: "Home",
      cancel: "Cancel",
      saveChanges: "Save Changes",
      editProfile: "Edit Profile",
      profileInfo: "Profile Information",
      fullName: "Full Name",
      phoneNumber: "Phone Number",
      district: "District",
      age: "Age",
      occupation: "Occupation",
      caste: "Caste / Category",
      annualIncome: "Annual Household Income",
      forgotPassword: "Forgot Password?",
      phoneCannotChange: "Phone number cannot be changed",
      emailNotVerified: "Email not verified — click \"Send OTP\" to verify",
      
      // Help
      helpMeApply: "Help Me Apply",
      needHelp: "Need Help?",
      contactUs: "Contact Us",
      faq: "FAQ",
      
      // Forgot Password
      resetPassword: "Reset Password",
      forgotPasswordDesc: "Enter your registered phone number. We'll send a password reset link to your registered email.",
      sendResetLink: "Send Reset Link",
      sending: "Sending...",
      resetLinkSent: "Reset link sent!",
      resetLinkSentDesc: "A password reset link has been sent to your registered email:",
      linkExpiresIn1Hour: "The link expires in 1 hour.",
      requestReceived: "Request received",
      resetLinkGenericMsg: "If an account with this phone number exists and has a verified email, a reset link has been sent.",
      backToLogin: "Back to Login",
      enterValidPhone: "Enter a valid 10-digit phone number",

      // Profile Page
      changePassword: "Change Password",
      currentPassword: "Current Password",
      newPassword: "New Password",
      confirmNewPassword: "Confirm New Password",
      updatePassword: "Update Password",
      updatingPassword: "Updating...",
      emailAddress: "Email Address",
      emailVerified: "Email Verified",
      sendOtp: "Send OTP",
      verifyOtp: "Verify OTP",
      resendOtp: "Resend OTP",
      enterOtp: "Enter 6-digit OTP",
      notProvided: "Not provided",
      saving: "Saving...",

      // Government Jobs
      governmentJobs: "Jobs",
      searchJobsPlaceholder: "Search jobs by title, organisation, location...",
      foundJobs: "Found",
      govJobs: "government jobs",
      sortLatest: "Expected",
      sortExpected: "Expected",
      sortActive: "Active",
      sortDeadline: "Active",
      sortVacancies: "Vacancies",
      location: "Location",
      allIndia: "All India",
      andhraPradesh: "Andhra Pradesh",
      telangana: "Telangana",
      karnataka: "Karnataka",
      noJobsFound: "No jobs found",
      tryAdjustingJobs: "Try adjusting your search or filters",
      clearAllJobFilters: "Clear All Filters",
      verified: "Verified",
      expected: "Expected",
      rolling: "Rolling",
      daysLeft: "d left",
      expired: "Expired",
      posts: "posts",
      viewOfficialNotification: "View Official Notification",
      saveJob: "Save Job",
      share: "Share",
      organisation: "Organisation",
      qualification: "Qualification",
      vacancies: "Vacancies",
      salary: "Salary",
      ageLimit: "Age Limit",
      source: "Source",
      years: "years",
      daysRemaining: "days remaining",
      lastDate: "Last Date",
      rollingApplication: "Rolling Application",
      expectedRecruitment: "Expected Recruitment",
      expectedRecruitmentDesc: "This is based on the annual recruitment calendar. Dates and vacancies are indicative. Please check the official website for confirmed notifications.",
      recommendedTechJobs: "Recommended Tech Jobs",
      basedOnProfile: "Based on your IT/Technology profile",
      viewAll: "View All",
      savedGovJobs: "Saved Government Jobs",
      noSavedJobsYet: "No saved jobs yet",
      saveJobsHint: "Save government jobs from the Schemes tab to see them here",
      jobsDisclaimer: "KnowMySchemes aggregates government job notifications from official sources for informational purposes only. We are not a recruitment agency. Always verify details on the official website before applying. We are not responsible for any changes in dates, eligibility, or other details.",
      disclaimer: "Disclaimer",
    }
  },
  
  // =====================
  // తెలుగు (TELUGU) - Native Translations
  // =====================
  te: {
    translation: {
      // App Branding
      appName: "ప్రభుత్వ పథకాలు",
      tagline: "పౌరుల సాధికారత",
      
      // Common Actions
      login: "లాగిన్",
      logout: "లాగౌట్",
      signup: "నమోదు చేసుకోండి",
      home: "హోమ్",
      back: "వెనక్కి",
      search: "వెతకండి",
      filters: "వడపోతలు",
      clear: "తీసివేయండి",
      viewDetails: "వివరాలు చూడండి",
      applyNow: "దరఖాస్తు చేయండి",
      saved: "భద్రపరచినవి",
      all: "అన్నీ",
      news: "వార్తలు",
      loading: "లోడ్ అవుతోంది...",
      submit: "సమర్పించండి",
      cancel: "రద్దు చేయండి",
      save: "భద్రపరచండి",
      delete: "తొలగించండి",
      edit: "మార్చండి",
      close: "మూసివేయండి",
      yes: "అవును",
      no: "కాదు",
      ok: "సరే",
      continue: "కొనసాగించండి",
      note: "గమనిక",
      adminDashboard: "అడ్మిన్ డాష్‌బోర్డ్",
      
      // Login Page
      welcomeBack: "తిరిగి స్వాగతం",
      loginToAccess: "ప్రభుత్వ పథకాలు చూడటానికి లాగిన్ చేయండి",
      phoneNumber: "ఫోన్ నంబర్",
      phonePlaceholder: "10 అంకెల మొబైల్ నంబర్",
      password: "పాస్‌వర్డ్",
      passwordPlaceholder: "మీ పాస్‌వర్డ్ నమోదు చేయండి",
      rememberMe: "నన్ను గుర్తుంచుకో",
      loggingIn: "లాగిన్ అవుతోంది...",
      noAccount: "ఖాతా లేదా?",
      forgotPassword: "పాస్‌వర్డ్ మర్చిపోయారా?",
      
      // Signup Page
      createAccount: "కొత్త ఖాతా సృష్టించండి",
      signupTagline: "మీకు తగిన పథకాలను కనుగొనండి",
      fullName: "పూర్తి పేరు",
      fullNamePlaceholder: "మీ పూర్తి పేరు నమోదు చేయండి",
      confirmPassword: "పాస్‌వర్డ్ నిర్ధారించండి",
      confirmPasswordPlaceholder: "పాస్‌వర్డ్ మళ్ళీ నమోదు చేయండి",
      state: "రాష్ట్రం",
      selectState: "రాష్ట్రం ఎంచుకోండి",
      district: "జిల్లా",
      selectDistrict: "జిల్లా ఎంచుకోండి",
      age: "వయస్సు",
      agePlaceholder: "మీ వయస్సు నమోదు చేయండి",
      selectAge: "మీ వయస్సు ఎంచుకోండి",
      casteCategory: "కుల వర్గం",
      selectCategory: "వర్గం ఎంచుకోండి",
      occupation: "వృత్తి",
      selectOccupation: "వృత్తి ఎంచుకోండి",
      specifyOccupation: "మీ వృత్తిని తెలియజేయండి",
      enterOccupation: "వృత్తి నమోదు చేయండి",
      gender: "లింగం",
      selectGender: "లింగం ఎంచుకోండి",
      genderMale: "పురుషుడు",
      genderFemale: "స్త్రీ",
      genderOther: "ఇతర",
      householdIncome: "కుటుంబ వార్షిక ఆదాయం",
      selectIncome: "ఆదాయ పరిధి ఎంచుకోండి",
      creatingAccount: "ఖాతా సృష్టిస్తోంది...",
      alreadyHaveAccount: "ఇప్పటికే ఖాతా ఉందా?",
      signupCaution: "దయచేసి ఖచ్చితమైన వ్యక్తిగత మరియు ఆదాయ వివరాలను నమోదు చేయండి. తప్పు సమాచారం మీ అర్హతను ప్రభావితం చేయవచ్చు.",
      termsAgreementLabel: "నేను చదివి అంగీకరిస్తున్నాను",
      termsConditionsPrivacy: "నియమాలు, షరతులు మరియు గోప్యతా విధానం",
      termsNotAccepted: "నమోదు చేయడానికి మీరు నియమాలు, షరతులు మరియు గోప్యతా విధానాన్ని అంగీకరించాలి",
      termsAcceptedConfirm: "మీరు నియమాలను అంగీకరించారు",
      termsModalTitle: "నియమాలు, షరతులు & గోప్యతా విధానం",
      acceptTerms: "నేను నియమాలను అంగీకరిస్తున్నాను",
      
      // Occupation Options
      occupationAgriculture: "వ్యవసాయం/రైతు",
      occupationIT: "సాఫ్ట్‌వేర్/టెక్నాలజీ",
      occupationBanking: "బ్యాంకింగ్/ఆర్థిక రంగం",
      occupationBusiness: "వ్యాపారం/స్వయం ఉపాధి",
      occupationAccountant: "అకౌంటెంట్",
      occupationHomeMaker: "గృహిణి",
      occupationGovernment: "ప్రభుత్వ ఉద్యోగి",
      occupationHealthCare: "వైద్య రంగం",
      occupationCrafts: "చేతివృత్తులు/కళాకారుడు",
      occupationManualLabor: "రోజువారీ కూలి",
      occupationCreativeMedia: "కళలు/మీడియా",
      occupationStudent: "విద్యార్థి",
      occupationSalesRetail: "అమ్మకాలు/రిటైల్",
      occupationTeachers: "ఉపాధ్యాయుడు/ప్రొఫెసర్",
      occupationOthers: "ఇతర వృత్తులు",
      
      // Home Page
      indiaLargestPortal: "భారతదేశంలో అతిపెద్ద సంక్షేమ పథకాల వెబ్‌సైట్",
      transformingLives: "జీవితాలను మారుస్తూ",
      incredibleIndia: "అద్భుతమైన భారతం",
      reachingHouseholds: "చేరుకుంటున్నాము",
      householdsText: "30 కోట్లకు పైగా కుటుంబాలకు",
      acrossIndia: "దేశవ్యాప్తంగా సంక్షేమ పథకాలతో",
      discoverBenefits: "మీకు రావాల్సిన",
      youDeserve: "లాభాలను తెలుసుకోండి",
      findBenefitsNow: "మీ లాభాలు ఇప్పుడే కనుగొనండి",
      totalPopulation: "మొత్తం జనాభా",
      nationalCoverage: "జాతీయ వ్యాప్తి",
      householdsReached: "లబ్ధి పొందిన కుటుంబాలు",
      directBenefitTransfers: "నేరుగా బదిలీలు",
      realPeopleRealImpact: "నిజమైన ప్రభావం",
      meetTheFaces: "లబ్ధిదారులను కలవండి",
      farmers: "రైతులు",
      women: "మహిళలు",
      students: "విద్యార్థులు",
      seniors: "వృద్ధులు",
      everyMinuteCounts: "ప్రతి నిమిషం విలువైనది",
      transferredPerMin: "నిమిషానికి బదిలీ",
      familiesPerMin: "నిమిషానికి కుటుంబాలు",
      support247: "24 గంటలూ సహాయం",
      dontMissOut: "మీ లాభాలను కోల్పోకండి",
      joinMillions: "లక్షలాది మంది జీవితాలను మార్చిన పథకాలను కనుగొనండి",
      startExploringNow: "ఇప్పుడే అన్వేషించండి",
      exploreSchemes: "పథకాలు & ఉద్యోగాలు",
      navImpact: "KMS Smart",
      loginForImpact: "KMS Smart ఉపయోగించడానికి లాగిన్ చేయండి",
      
      // Schemes Page
      discoverYourBenefits: "మీ లాభాలను కనుగొనండి",
      searchThrough: "వెతకండి",
      activeSchemes: "క్రియాశీల పథకాలు",
      searchSchemes: "పథకాలు వెతకండి...",
      sector: "రంగం",
      selectSector: "రంగం ఎంచుకోండి",
      category: "వర్గం",
      type: "రకం",
      selectType: "రకం ఎంచుకోండి",
      found: "దొరికాయి",
      schemes: "పథకాలు",
      scheme: "పథకం",
      freeSchemes: "ఉచిత పథకాలు",
      premiumSchemes: "ప్రీమియం పథకాలు",
      locked: "లాక్ చేయబడింది",
      unlocked: "అన్‌లాక్ చేయబడింది",
      loadingSchemes: "పథకాలు లోడ్ అవుతున్నాయి...",
      noSchemesFound: "పథకాలు కనుగొనబడలేదు",
      tryAdjusting: "వడపోతలు మార్చి మళ్ళీ ప్రయత్నించండి",
      clearAllFilters: "అన్ని వడపోతలు తొలగించండి",
      getWhatYouDeserve: "మీకు రావాల్సినవి పొందండి!",
      dontMissOutOnBenefits: "ప్రభుత్వ లాభాలను కోల్పోకండి. మీకు అర్హత ఉన్న పథకాలను కనుగొని దరఖాస్తు చేసుకోవడంలో మేము సహాయం చేస్తాము.",
      statesCovered: "రాష్ట్రాలు",
      newSchemesAdded: "కొత్త పథకాలు జోడించబడ్డాయి!",
      farmerSchemes: "రైతు పథకాలు",
      educationScholarships: "విద్య & స్కాలర్‌షిప్‌లు",
      womenEmpowerment: "మహిళా సాధికారత",
      healthWelfare: "ఆరోగ్యం & సంక్షేమం",
      free: "ఉచితం",
      premium: "ప్రీమియం",
      premiumScheme: "ప్రీమియం పథకం",
      premiumSchemeAccessDesc: "ఇది ప్రీమియం పథకం. మీ దరఖాస్తుకు నిపుణుల సహాయం పొందడానికి సబ్‌స్క్రైబ్ చేయండి.",
      unlockNow: "ఇప్పుడే అన్‌లాక్ చేయండి",
      getAssistance: "సహాయం పొందండి",
      eligible: "అర్హులు",
      youAreEligibleFor: "మీకు అర్హత ఉంది",
      
      // Scheme Details
      eligibilityCriteria: "అర్హత నియమాలు",
      benefits: "లాభాలు",
      requiredDocuments: "అవసరమైన పత్రాలు",
      howToApply: "దరఖాస్తు విధానం",
      deadline: "చివరి తేదీ",
      browseMoreSchemes: "మరిన్ని పథకాలు చూడండి",
      casteEligibility: "కుల అర్హత",
      schemeNotFound: "పథకం కనుగొనబడలేదు",
      schemeNotFoundDesc: "ఈ పథకం అందుబాటులో లేదు",
      backToSchemes: "పథకాలకు తిరిగి వెళ్ళండి",
      schemeDetails: "పథకం వివరాలు",
      
      // Saved Schemes
      yourSavedSchemes: "భద్రపరచిన పథకాలు",
      youHave: "మీ వద్ద",
      savedSchemes: "భద్రపరచిన పథకాలు ఉన్నాయి",
      savedScheme: "భద్రపరచిన పథకం ఉంది",
      noSavedSchemesYet: "ఇంకా భద్రపరచిన పథకాలు లేవు",
      startExploring: "పథకాలు చూసి మీకు నచ్చినవి భద్రపరచండి",
      browseAllSchemes: "అన్ని పథకాలు చూడండి",
      savedOn: "భద్రపరచిన తేదీ",
      removeFromSaved: "తొలగించండి",
      allSchemes: "అన్ని పథకాలు",
      
      // Notifications
      notifications: "నోటిఫికేషన్లు",
      markAllRead: "అన్నీ చదివినట్లు గుర్తించు",
      noNotificationsYet: "నోటిఫికేషన్లు లేవు",
      notifyAboutSchemes: "కొత్త పథకాల గురించి మీకు తెలియజేస్తాము",
      justNow: "ఇప్పుడే",
      minutesAgo: "{{count}} నిమిషాల క్రితం",
      hoursAgo: "{{count}} గంటల క్రితం",
      daysAgo: "{{count}} రోజుల క్రితం",
      
      // Premium/Subscription
      premiumAssistance: "ప్రీమియం సహాయం",
      premiumAssistanceRequired: "ప్రీమియం సహాయం అవసరం",
      unlockPremium: "ప్రీమియం అన్‌లాక్ చేయండి",
      subscribeNow: "ఇప్పుడే చందా చేరండి",
      viewPlans: "ప్లాన్లు చూడండి",
      maybeLater: "తర్వాత చూస్తాను",
      viewFreeSchemes: "ఉచిత పథకాలు చూడండి",
      teamWillAssist: "మా బృందం సహాయం చేస్తుంది",
      ourTeamWillContact: "మా బృందం మిమ్మల్ని సంప్రదిస్తుంది",
      unlockHighValueSchemes: "అధిక విలువ పథకాలను అన్‌లాక్ చేయండి",
      expertAssistanceDesc: "అర్హత ధృవీకరణ, ఫారం నింపడం, పూర్తి దరఖాస్తు సహాయంలో మా నిపుణులు మీకు సహాయం చేస్తారు.",
      eligibilityVerification: "అన్ని పథకాలకు అర్హత ధృవీకరణ",
      formFillingAssistance: "ఫారం నింపడం & పత్రాల సహాయం",
      teamContactsAfterPayment: "చెల్లింపు తర్వాత బృందం మిమ్మల్ని సంప్రదిస్తుంది",
      startingFrom: "ప్రారంభ ధర",
      perYear: "/సంవత్సరానికి",
      assistanceNote: "మేము సహాయ సేవలు మాత్రమే అందిస్తాము. చందా పథకం ఆమోదానికి హామీ ఇవ్వదు.",
      paymentSuccessful: "చెల్లింపు విజయవంతం! 🎉",
      teamWillContactSoon: "మీకు అర్హత ఉన్న పథకాలలో సహాయం అందించడానికి మా బృందం త్వరలో మిమ్మల్ని సంప్రదిస్తుంది.",
      whatHappensNext: "తదుపరి ఏమి జరుగుతుంది:",
      keepPhoneAvailable: "దయచేసి మీ ఫోన్‌ను అందుబాటులో ఉంచండి.",
      documentGuidance: "డాక్యుమెంట్ అప్‌లోడ్ గైడెన్స్",
      applicationSupport: "దరఖాస్తు సపోర్ట్",
      eligibilityCheck: "అర్హత ధృవీకరణ",
      eligibilityCheckDesc: "అన్ని వర్తించే ప్రభుత్వ పథకాలకు మీ అర్హతను మేము ధృవీకరిస్తాము",
      formFilling: "ఫారం నింపడం",
      formFillingDesc: "దరఖాస్తు ఫారాలు మరియు డాక్యుమెంట్ తయారీలో నిపుణుల సహాయం",
      phoneSupport: "ఫోన్ సపోర్ట్",
      phoneSupportDesc: "మొత్తం ప్రక్రియలో మీకు మార్గదర్శనం చేయడానికి అంకితమైన సపోర్ట్ టీమ్",
      getExpertHelp: "మీ దరఖాస్తులకు నిపుణుల సహాయం పొందండి",
      expertHelpDesc: "అర్హత తనిఖీ నుండి డాక్యుమెంట్ సమర్పణ వరకు మొత్తం దరఖాస్తు ప్రక్రియలో మా నిపుణుల బృందం మీకు మార్గదర్శనం చేస్తుంది.",
      activeSubscription: "యాక్టివ్ సబ్‌స్క్రిప్షన్",
      bestValue: "ఉత్తమ విలువ",
      selectPlan: "ప్లాన్ ఎంచుకోండి",
      alreadySubscribed: "ఇప్పటికే సబ్‌స్క్రైబ్ చేసారు",
      importantInformation: "ముఖ్యమైన సమాచారం",
      termsAndConditions: "నిబంధనలు & షరతులు",
      importantDisclaimer: "ముఖ్యమైన నిరాకరణ",
      disclaimerText: "మేము ప్రభుత్వ పథకాలు లేదా ప్రయోజనాలను విక్రయించము. సబ్‌స్క్రిప్షన్ రుసుము సర్వీస్ సహాయానికి మాత్రమే.",
      serviceIncludes: "సర్వీస్‌లో ఇవి ఉన్నాయి:",
      importantPoints: "ముఖ్యమైన అంశాలు:",
      subscriptionOptions: "సబ్‌స్క్రిప్షన్ ఆప్షన్లు:",
      refundPolicy: "రీఫండ్ పాలసీ:",
      refundPolicyText: "చట్టం ద్వారా అవసరమైతే తప్ప విజయవంతమైన చెల్లింపు తర్వాత రీఫండ్లు లేవు.",
      agreeAndPay: "అంగీకరించి చెల్లించండి",
      processing: "ప్రాసెస్ అవుతోంది...",
      
      // Filters
      central: "కేంద్రం",
      stateType: "రాష్ట్రం",
      general: "జనరల్",
      sc: "ఎస్సీ",
      st: "ఎస్టీ",
      obc: "ఓబీసీ",
      
      // Sector Names
      sectorAgriculture: "వ్యవసాయం",
      sectorHealth: "ఆరోగ్యం",
      sectorHousing: "గృహ నిర్మాణం",
      sectorEducation: "విద్య",
      sectorEmployment: "ఉపాధి",
      sectorBusiness: "వ్యాపారం",
      sectorWomenChild: "మహిళలు & పిల్లలు",
      sectorSocialWelfare: "సామాజిక సంక్షేమం",
      sectorFinancial: "ఆర్థిక సేవలు",
      sectorRural: "గ్రామీణాభివృద్ధి",
      sectorUrban: "పట్టణాభివృద్ధి",
      sectorSkill: "నైపుణ్యాభివృద్ధి",
      sectorSkillsEmployment: "నైపుణ్యాలు & ఉపాధి",
      sectorFood: "ఆహార భద్రత",
      sectorInsurance: "బీమా",
      sectorPension: "పెన్షన్",
      
      // States
      andhrapradesh: "ఆంధ్రప్రదేశ్",
      telangana: "తెలంగాణ",
      karnataka: "కర్ణాటక",
      tamilnadu: "తమిళనాడు",
      kerala: "కేరళ",
      maharashtra: "మహారాష్ట్ర",
      
      // Language
      language: "భాష",
      english: "English",
      telugu: "తెలుగు",
      hindi: "हिंदी",
      kannada: "ಕನ್ನಡ",
      selectLanguage: "భాష ఎంచుకోండి",
      
      // News Page
      latestNews: "తాజా వార్తలు",
      latestIndiaNews: "తాజా భారతదేశ వార్తలు",
      newsSubtitle: "ప్రభుత్వ పథకాలు, విధానాలు & ప్రాంతీయ నవీకరణలు",
      newsUpdates: "వార్తా నవీకరణలు",
      readMore: "మరింత చదవండి",
      lastUpdated: "చివరిగా నవీకరించబడింది",
      refreshNews: "వార్తలు రిఫ్రెష్ చేయండి",
      searchNews: "వార్తలు వెతకండి...",
      loadingNews: "వార్తలు లోడ్ అవుతున్నాయి...",
      noNewsFound: "వార్తలు కనుగొనబడలేదు",
      tryDifferentSearch: "వేరే పదంతో ప్రయత్నించండి",
      newsWillBeUpdated: "వార్తలు త్వరలో నవీకరించబడతాయి",
      
      // Profile
      profile: "ప్రొఫైల్",
      myProfile: "నా ప్రొఫైల్",
      editProfile: "ప్రొఫైల్ మార్చండి",
      saveChanges: "మార్పులు భద్రపరచండి",
      deleteAccount: "ఖాతా తొలగించండి",
      personalInfo: "వ్యక్తిగత సమాచారం",
      accountSettings: "ఖాతా సెట్టింగ్‌లు",
      
      // Errors & Messages
      error: "లోపం",
      success: "విజయవంతం",
      somethingWentWrong: "ఏదో తప్పు జరిగింది",
      tryAgain: "మళ్ళీ ప్రయత్నించండి",
      pleaseWait: "దయచేసి వేచి ఉండండి...",
      noDataFound: "సమాచారం కనుగొనబడలేదు",
      connectionError: "కనెక్షన్ లోపం",
      
      // Common UI
      back: "తిరిగి",
      home: "హోమ్",
      cancel: "రద్దు చేయండి",
      saveChanges: "మార్పులు సేవ్ చేయండి",
      editProfile: "ప్రొఫైల్ సవరించండి",
      profileInfo: "ప్రొఫైల్ సమాచారం",
      fullName: "పూర్తి పేరు",
      phoneNumber: "ఫోన్ నంబర్",
      district: "జిల్లా",
      age: "వయస్సు",
      occupation: "వృత్తి",
      caste: "కులం / వర్గం",
      annualIncome: "వార్షిక ఇంటి ఆదాయం",
      forgotPassword: "పాస్‌వర్డ్ మర్చిపోయారా?",
      phoneCannotChange: "ఫోన్ నంబర్ మార్చడం సాధ్యం కాదు",
      emailNotVerified: "ఇమెయిల్ ధృవీకరించబడలేదు — ధృవీకరించడానికి \"OTP పంపండి\" క్లిక్ చేయండి",
      
      // Help
      helpMeApply: "దరఖాస్తులో సహాయం",
      needHelp: "సహాయం కావాలా?",
      contactUs: "మమ్మల్ని సంప్రదించండి",
      faq: "తరచుగా అడిగే ప్రశ్నలు",
      
      // Forgot Password
      resetPassword: "పాస్‌వర్డ్ రీసెట్ చేయండి",
      forgotPasswordDesc: "మీ నమోదిత ఫోన్ నంబర్ నమోదు చేయండి. మీ నమోదిత ఇమెయిల్‌కు పాస్‌వర్డ్ రీసెట్ లింక్ పంపబడుతుంది.",
      sendResetLink: "రీసెట్ లింక్ పంపండి",
      sending: "పంపుతోంది...",
      resetLinkSent: "రీసెట్ లింక్ పంపబడింది!",
      resetLinkSentDesc: "మీ నమోదిత ఇమెయిల్‌కు పాస్‌వర్డ్ రీసెట్ లింక్ పంపబడింది:",
      linkExpiresIn1Hour: "లింక్ 1 గంటలో గడువు ముగుస్తుంది.",
      requestReceived: "అభ్యర్థన స్వీకరించబడింది",
      resetLinkGenericMsg: "ఈ ఫోన్ నంబర్‌తో ఖాతా ఉంటే మరియు ధృవీకరించబడిన ఇమెయిల్ ఉంటే, రీసెట్ లింక్ పంపబడింది.",
      backToLogin: "లాగిన్‌కు తిరిగి వెళ్ళండి",
      enterValidPhone: "చెల్లుబాటు అయ్యే 10 అంకెల ఫోన్ నంబర్ నమోదు చేయండి",
      
      // Profile Page
      changePassword: "పాస్‌వర్డ్ మార్చండి",
      currentPassword: "ప్రస్తుత పాస్‌వర్డ్",
      newPassword: "కొత్త పాస్‌వర్డ్",
      confirmNewPassword: "కొత్త పాస్‌వర్డ్ నిర్ధారించండి",
      updatePassword: "పాస్‌వర్డ్ నవీకరించండి",
      updatingPassword: "నవీకరిస్తోంది...",
      emailAddress: "ఇమెయిల్ చిరునామా",
      emailVerified: "ఇమెయిల్ ధృవీకరించబడింది",
      sendOtp: "OTP పంపండి",
      verifyOtp: "OTP ధృవీకరించండి",
      resendOtp: "OTP మళ్ళీ పంపండి",
      enterOtp: "6 అంకెల OTP నమోదు చేయండి",
      notProvided: "అందించలేదు",
      saving: "భద్రపరుస్తోంది...",

      // Government Jobs
      governmentJobs: "ఉద్యోగాలు",
      searchJobsPlaceholder: "టైటిల్, సంస్థ, ప్రదేశం ద్వారా ఉద్యోగాలు వెతకండి...",
      foundJobs: "కనుగొనబడింది",
      govJobs: "ప్రభుత్వ ఉద్యోగాలు",
      sortLatest: "ఊహించినవి",
      sortExpected: "ఊహించినవి",
      sortActive: "యాక్టివ్",
      sortDeadline: "యాక్టివ్",
      sortVacancies: "ఖాళీలు",
      location: "ప్రదేశం",
      allIndia: "అఖిల భారతం",
      andhraPradesh: "ఆంధ్ర ప్రదేశ్",
      telangana: "తెలంగాణ",
      karnataka: "కర్ణాటక",
      noJobsFound: "ఉద్యోగాలు కనుగొనబడలేదు",
      tryAdjustingJobs: "మీ వెతకడం లేదా వడపోతలు సవరించండి",
      clearAllJobFilters: "అన్ని వడపోతలు తీసివేయండి",
      verified: "ధృవీకరించబడింది",
      expected: "ఊహించబడింది",
      rolling: "నిరంతరం",
      daysLeft: "రోజులు మిగిలి",
      expired: "గడువు ముగిసింది",
      posts: "పోస్టులు",
      viewOfficialNotification: "అధికారిక నోటిఫికేషన్ చూడండి",
      saveJob: "ఉద్యోగం భద్రపరచండి",
      share: "షేర్ చేయండి",
      organisation: "సంస్థ",
      qualification: "అర్హత",
      vacancies: "ఖాళీలు",
      salary: "జీతం",
      ageLimit: "వయస్సు పరిమితి",
      source: "మూలం",
      years: "సంవత్సరాలు",
      daysRemaining: "రోజులు మిగిలి",
      lastDate: "చివరి తేదీ",
      rollingApplication: "నిరంతర దరఖాస్తు",
      expectedRecruitment: "ఊహించిన నియామకం",
      expectedRecruitmentDesc: "ఇది వార్షిక నియామక క్యాలెండర్ ఆధారంగా ఉంది. తేదీలు మరియు ఖాళీలు సూచనాత్మకం. నిర్ధారించిన నోటిఫికేషన్ల కోసం అధికారిక వెబ్‌సైట్ తనిఖీ చేయండి.",
      recommendedTechJobs: "సిఫార్సు చేయబడిన టెక్ ఉద్యోగాలు",
      basedOnProfile: "మీ IT/టెక్నాలజీ ప్రొఫైల్ ఆధారంగా",
      viewAll: "అన్నీ చూడండి",
      savedGovJobs: "భద్రపరచిన ప్రభుత్వ ఉద్యోగాలు",
      noSavedJobsYet: "ఇంకా భద్రపరచిన ఉద్యోగాలు లేవు",
      saveJobsHint: "ఇక్కడ చూడటానికి స్కీమ్‌ల ట్యాబ్ నుండి ప్రభుత్వ ఉద్యోగాలను భద్రపరచండి",
      jobsDisclaimer: "KnowMySchemes సమాచార ప్రయోజనాల కోసం అధికారిక మూలాల నుండి ప్రభుత్వ ఉద్యోగ నోటిఫికేషన్లను సేకరిస్తుంది. మేము నియామక సంస్థ కాదు. దరఖాస్తు చేసే ముందు అధికారిక వెబ్‌సైట్‌లో వివరాలను ధృవీకరించండి.",
      disclaimer: "నిరాకరణ",
    }
  },
  
  // =====================
  // हिंदी (HINDI) - Native Translations
  // =====================
  hi: {
    translation: {
      // App Branding
      appName: "सरकारी योजनाएं",
      tagline: "नागरिक सशक्तिकरण",
      
      // Common Actions
      login: "लॉगिन",
      logout: "लॉगआउट",
      signup: "साइन अप करें",
      home: "होम",
      back: "वापस",
      search: "खोजें",
      filters: "फ़िल्टर",
      clear: "हटाएं",
      viewDetails: "विवरण देखें",
      applyNow: "अभी आवेदन करें",
      saved: "सहेजे गए",
      all: "सभी",
      news: "समाचार",
      loading: "लोड हो रहा है...",
      submit: "जमा करें",
      cancel: "रद्द करें",
      save: "सहेजें",
      delete: "हटाएं",
      edit: "संपादित करें",
      close: "बंद करें",
      yes: "हाँ",
      no: "नहीं",
      ok: "ठीक है",
      continue: "जारी रखें",
      note: "नोट",
      adminDashboard: "एडमिन डैशबोर्ड",
      
      // Login Page
      welcomeBack: "वापसी पर स्वागत है",
      loginToAccess: "सरकारी योजनाएं देखने के लिए लॉगिन करें",
      phoneNumber: "मोबाइल नंबर",
      phonePlaceholder: "10 अंकों का मोबाइल नंबर",
      password: "पासवर्ड",
      passwordPlaceholder: "अपना पासवर्ड दर्ज करें",
      rememberMe: "मुझे याद रखें",
      loggingIn: "लॉगिन हो रहा है...",
      noAccount: "खाता नहीं है?",
      forgotPassword: "पासवर्ड भूल गए?",
      
      // Signup Page
      createAccount: "नया खाता बनाएं",
      signupTagline: "आपके लिए उपयुक्त योजनाएं खोजें",
      fullName: "पूरा नाम",
      fullNamePlaceholder: "अपना पूरा नाम दर्ज करें",
      confirmPassword: "पासवर्ड की पुष्टि करें",
      confirmPasswordPlaceholder: "पासवर्ड दोबारा दर्ज करें",
      state: "राज्य",
      selectState: "राज्य चुनें",
      district: "जिला",
      selectDistrict: "जिला चुनें",
      age: "उम्र",
      agePlaceholder: "अपनी उम्र दर्ज करें",
      selectAge: "अपनी उम्र चुनें",
      casteCategory: "जाति श्रेणी",
      selectCategory: "श्रेणी चुनें",
      occupation: "व्यवसाय",
      selectOccupation: "व्यवसाय चुनें",
      specifyOccupation: "अपना व्यवसाय बताएं",
      enterOccupation: "व्यवसाय दर्ज करें",
      gender: "लिंग",
      selectGender: "लिंग चुनें",
      genderMale: "पुरुष",
      genderFemale: "महिला",
      genderOther: "अन्य",
      householdIncome: "पारिवारिक वार्षिक आय",
      selectIncome: "आय सीमा चुनें",
      creatingAccount: "खाता बन रहा है...",
      alreadyHaveAccount: "पहले से खाता है?",
      signupCaution: "कृपया सटीक व्यक्तिगत और आय विवरण दर्ज करें। गलत जानकारी आपकी पात्रता को प्रभावित कर सकती है।",
      termsAgreementLabel: "मैंने पढ़ा और सहमत हूं",
      termsConditionsPrivacy: "नियम, शर्तें और गोपनीयता नीति",
      termsNotAccepted: "पंजीकरण के लिए आपको नियम, शर्तें और गोपनीयता नीति स्वीकार करनी होगी",
      termsAcceptedConfirm: "आपने शर्तें स्वीकार कर ली हैं",
      termsModalTitle: "नियम, शर्तें और गोपनीयता नीति",
      acceptTerms: "मैं शर्तें स्वीकार करता/करती हूं",
      
      // Occupation Options
      occupationAgriculture: "खेती/किसानी",
      occupationIT: "सॉफ्टवेयर/टेक्नोलॉजी",
      occupationBanking: "बैंकिंग/वित्त क्षेत्र",
      occupationBusiness: "व्यापार/स्वरोजगार",
      occupationAccountant: "लेखाकार",
      occupationHomeMaker: "गृहिणी",
      occupationGovernment: "सरकारी कर्मचारी",
      occupationHealthCare: "स्वास्थ्य सेवा",
      occupationCrafts: "हस्तशिल्प/कारीगर",
      occupationManualLabor: "दैनिक मजदूर",
      occupationCreativeMedia: "कला/मीडिया",
      occupationStudent: "छात्र",
      occupationSalesRetail: "बिक्री/खुदरा",
      occupationTeachers: "शिक्षक/प्रोफेसर",
      occupationOthers: "अन्य व्यवसाय",
      
      // Home Page
      indiaLargestPortal: "भारत का सबसे बड़ा कल्याणकारी योजना पोर्टल",
      transformingLives: "जीवन बदल रहे हैं",
      incredibleIndia: "अतुल्य भारत",
      reachingHouseholds: "पहुंच रहे हैं",
      householdsText: "30 करोड़ से अधिक परिवारों तक",
      acrossIndia: "देशभर में कल्याणकारी योजनाओं के साथ",
      discoverBenefits: "आपको मिलने वाले",
      youDeserve: "लाभों को जानें",
      findBenefitsNow: "अपने लाभ अभी खोजें",
      totalPopulation: "कुल जनसंख्या",
      nationalCoverage: "राष्ट्रीय कवरेज",
      householdsReached: "लाभान्वित परिवार",
      directBenefitTransfers: "सीधे हस्तांतरण",
      realPeopleRealImpact: "वास्तविक प्रभाव",
      meetTheFaces: "लाभार्थियों से मिलें",
      farmers: "किसान",
      women: "महिलाएं",
      students: "छात्र",
      seniors: "वरिष्ठ नागरिक",
      everyMinuteCounts: "हर मिनट महत्वपूर्ण है",
      transferredPerMin: "प्रति मिनट हस्तांतरण",
      familiesPerMin: "प्रति मिनट परिवार",
      support247: "24 घंटे सहायता",
      dontMissOut: "अपने लाभ न गंवाएं",
      joinMillions: "लाखों लोगों की तरह जीवन बदलने वाली योजनाएं खोजें",
      startExploringNow: "अभी देखना शुरू करें",
      exploreSchemes: "योजनाएं & नौकरियां",
      navImpact: "KMS Smart",
      loginForImpact: "KMS Smart उपयोग करने के लिए लॉगिन करें",
      
      // Schemes Page
      discoverYourBenefits: "अपने लाभ खोजें",
      searchThrough: "खोजें",
      activeSchemes: "सक्रिय योजनाएं",
      searchSchemes: "योजनाएं खोजें...",
      sector: "क्षेत्र",
      selectSector: "क्षेत्र चुनें",
      category: "श्रेणी",
      type: "प्रकार",
      selectType: "प्रकार चुनें",
      found: "मिलीं",
      schemes: "योजनाएं",
      scheme: "योजना",
      freeSchemes: "मुफ्त योजनाएं",
      premiumSchemes: "प्रीमियम योजनाएं",
      locked: "लॉक है",
      unlocked: "अनलॉक है",
      loadingSchemes: "योजनाएं लोड हो रही हैं...",
      noSchemesFound: "कोई योजना नहीं मिली",
      tryAdjusting: "फ़िल्टर बदलकर फिर से खोजें",
      clearAllFilters: "सभी फ़िल्टर हटाएं",
      getWhatYouDeserve: "आपका हक़ पाएं!",
      dontMissOutOnBenefits: "सरकारी लाभ न गंवाएं। हम आपको योग्य योजनाएं खोजने और आवेदन करने में मदद करते हैं।",
      statesCovered: "राज्य",
      newSchemesAdded: "नई योजनाएं जोड़ी गईं!",
      farmerSchemes: "किसान योजनाएं",
      educationScholarships: "शिक्षा और छात्रवृत्ति",
      womenEmpowerment: "महिला सशक्तिकरण",
      healthWelfare: "स्वास्थ्य और कल्याण",
      free: "मुफ्त",
      premium: "प्रीमियम",
      premiumScheme: "प्रीमियम योजना",
      premiumSchemeAccessDesc: "यह एक प्रीमियम योजना है। अपने आवेदन में विशेषज्ञ सहायता प्राप्त करने के लिए सदस्यता लें।",
      unlockNow: "अभी अनलॉक करें",
      getAssistance: "सहायता प्राप्त करें",
      eligible: "योग्य",
      youAreEligibleFor: "आप योग्य हैं",
      
      // Scheme Details
      eligibilityCriteria: "पात्रता मानदंड",
      benefits: "लाभ",
      requiredDocuments: "आवश्यक दस्तावेज",
      howToApply: "आवेदन कैसे करें",
      deadline: "अंतिम तिथि",
      browseMoreSchemes: "और योजनाएं देखें",
      casteEligibility: "जाति पात्रता",
      schemeNotFound: "योजना नहीं मिली",
      schemeNotFoundDesc: "यह योजना उपलब्ध नहीं है",
      backToSchemes: "योजनाओं पर वापस जाएं",
      schemeDetails: "योजना विवरण",
      
      // Saved Schemes
      yourSavedSchemes: "सहेजी गई योजनाएं",
      youHave: "आपके पास",
      savedSchemes: "सहेजी गई योजनाएं हैं",
      savedScheme: "सहेजी गई योजना है",
      noSavedSchemesYet: "अभी कोई योजना नहीं सहेजी",
      startExploring: "योजनाएं देखें और पसंदीदा सहेजें",
      browseAllSchemes: "सभी योजनाएं देखें",
      savedOn: "सहेजने की तारीख",
      removeFromSaved: "हटाएं",
      allSchemes: "सभी योजनाएं",
      
      // Notifications
      notifications: "सूचनाएं",
      markAllRead: "सब पढ़ी हुई",
      noNotificationsYet: "कोई सूचना नहीं",
      notifyAboutSchemes: "नई योजनाओं की जानकारी मिलेगी",
      justNow: "अभी",
      minutesAgo: "{{count}} मिनट पहले",
      hoursAgo: "{{count}} घंटे पहले",
      daysAgo: "{{count}} दिन पहले",
      
      // Premium/Subscription
      premiumAssistance: "प्रीमियम सहायता",
      premiumAssistanceRequired: "प्रीमियम सहायता आवश्यक है",
      unlockPremium: "प्रीमियम अनलॉक करें",
      subscribeNow: "अभी सदस्यता लें",
      viewPlans: "प्लान देखें",
      maybeLater: "बाद में देखें",
      viewFreeSchemes: "मुफ्त योजनाएं देखें",
      teamWillAssist: "हमारी टीम मदद करेगी",
      ourTeamWillContact: "हमारी टीम आपसे संपर्क करेगी",
      unlockHighValueSchemes: "उच्च-मूल्य योजनाएं अनलॉक करें",
      expertAssistanceDesc: "हमारे विशेषज्ञ पात्रता सत्यापन, फॉर्म भरने और पूर्ण आवेदन सहायता में आपकी मदद करते हैं।",
      eligibilityVerification: "सभी योजनाओं के लिए पात्रता सत्यापन",
      formFillingAssistance: "फॉर्म भरने और दस्तावेज़ सहायता",
      teamContactsAfterPayment: "भुगतान के बाद टीम आपसे संपर्क करेगी",
      startingFrom: "शुरुआती कीमत",
      perYear: "/वर्ष",
      assistanceNote: "हम केवल सहायता सेवा प्रदान करते हैं। सदस्यता योजना स्वीकृति की गारंटी नहीं देती।",
      paymentSuccessful: "भुगतान सफल! 🎉",
      teamWillContactSoon: "आपकी योग्य योजनाओं में सहायता के लिए हमारी टीम जल्द ही आपसे संपर्क करेगी।",
      whatHappensNext: "आगे क्या होगा:",
      keepPhoneAvailable: "कृपया अपना फोन उपलब्ध रखें।",
      documentGuidance: "दस्तावेज़ अपलोड मार्गदर्शन",
      applicationSupport: "आवेदन सहायता",
      eligibilityCheck: "पात्रता जांच",
      eligibilityCheckDesc: "हम सभी लागू सरकारी योजनाओं के लिए आपकी पात्रता सत्यापित करते हैं",
      formFilling: "फॉर्म भरना",
      formFillingDesc: "आवेदन फॉर्म और दस्तावेज़ तैयारी में विशेषज्ञ सहायता",
      phoneSupport: "फोन सहायता",
      phoneSupportDesc: "पूरी प्रक्रिया में आपका मार्गदर्शन करने के लिए समर्पित सहायता टीम",
      getExpertHelp: "अपने आवेदनों के लिए विशेषज्ञ सहायता प्राप्त करें",
      expertHelpDesc: "हमारी विशेषज्ञ टीम पात्रता जांच से लेकर दस्तावेज़ जमा करने तक पूरी आवेदन प्रक्रिया में आपका मार्गदर्शन करेगी।",
      activeSubscription: "सक्रिय सदस्यता",
      bestValue: "सर्वोत्तम मूल्य",
      selectPlan: "प्लान चुनें",
      alreadySubscribed: "पहले से सदस्य हैं",
      importantInformation: "महत्वपूर्ण जानकारी",
      termsAndConditions: "नियम और शर्तें",
      importantDisclaimer: "महत्वपूर्ण अस्वीकरण",
      disclaimerText: "हम सरकारी योजनाएं या लाभ नहीं बेचते। सदस्यता शुल्क केवल सेवा सहायता के लिए है।",
      serviceIncludes: "सेवा में शामिल है:",
      importantPoints: "महत्वपूर्ण बिंदु:",
      subscriptionOptions: "सदस्यता विकल्प:",
      refundPolicy: "रिफंड नीति:",
      refundPolicyText: "कानून द्वारा आवश्यक होने तक सफल भुगतान के बाद कोई रिफंड नहीं।",
      agreeAndPay: "सहमत हों और भुगतान करें",
      processing: "प्रोसेस हो रहा है...",
      
      // Filters
      central: "केंद्रीय",
      stateType: "राज्य",
      general: "सामान्य",
      sc: "अनुसूचित जाति",
      st: "अनुसूचित जनजाति",
      obc: "अन्य पिछड़ा वर्ग",
      
      // Sector Names
      sectorAgriculture: "कृषि",
      sectorHealth: "स्वास्थ्य",
      sectorHousing: "आवास",
      sectorEducation: "शिक्षा",
      sectorEmployment: "रोजगार",
      sectorBusiness: "व्यापार",
      sectorWomenChild: "महिला एवं बाल",
      sectorSocialWelfare: "सामाजिक कल्याण",
      sectorFinancial: "वित्तीय सेवाएं",
      sectorRural: "ग्रामीण विकास",
      sectorUrban: "शहरी विकास",
      sectorSkill: "कौशल विकास",
      sectorSkillsEmployment: "कौशल और रोजगार",
      sectorFood: "खाद्य सुरक्षा",
      sectorInsurance: "बीमा",
      sectorPension: "पेंशन",
      
      // States
      andhrapradesh: "आंध्र प्रदेश",
      telangana: "तेलंगाना",
      karnataka: "कर्नाटक",
      tamilnadu: "तमिलनाडु",
      kerala: "केरल",
      maharashtra: "महाराष्ट्र",
      
      // Language
      language: "भाषा",
      english: "English",
      telugu: "తెలుగు",
      hindi: "हिंदी",
      kannada: "ಕನ್ನಡ",
      selectLanguage: "भाषा चुनें",
      
      // News Page
      latestNews: "ताज़ा समाचार",
      latestIndiaNews: "भारत के ताज़ा समाचार",
      newsSubtitle: "सरकारी योजनाएं, नीतियां और क्षेत्रीय अपडेट",
      newsUpdates: "समाचार अपडेट",
      readMore: "और पढ़ें",
      lastUpdated: "अंतिम अपडेट",
      refreshNews: "समाचार रिफ्रेश करें",
      searchNews: "समाचार खोजें...",
      loadingNews: "समाचार लोड हो रहे हैं...",
      noNewsFound: "कोई समाचार नहीं मिला",
      tryDifferentSearch: "कोई अन्य शब्द खोजें",
      newsWillBeUpdated: "समाचार जल्द ही अपडेट होंगे",
      
      // Profile
      profile: "प्रोफाइल",
      myProfile: "मेरा प्रोफाइल",
      editProfile: "प्रोफाइल संपादित करें",
      saveChanges: "बदलाव सहेजें",
      deleteAccount: "खाता हटाएं",
      personalInfo: "व्यक्तिगत जानकारी",
      accountSettings: "खाता सेटिंग्स",
      
      // Errors & Messages
      error: "त्रुटि",
      success: "सफल",
      somethingWentWrong: "कुछ गलत हो गया",
      tryAgain: "फिर से कोशिश करें",
      pleaseWait: "कृपया प्रतीक्षा करें...",
      noDataFound: "कोई डेटा नहीं मिला",
      connectionError: "कनेक्शन त्रुटि",
      
      // Common UI
      back: "वापस",
      home: "होम",
      cancel: "रद्द करें",
      saveChanges: "बदलाव सहेजें",
      editProfile: "प्रोफ़ाइल संपादित करें",
      profileInfo: "प्रोफ़ाइल जानकारी",
      fullName: "पूरा नाम",
      phoneNumber: "फ़ोन नंबर",
      district: "जिला",
      age: "उम्र",
      occupation: "पेशा",
      caste: "जाति / श्रेणी",
      annualIncome: "वार्षिक घरेलू आय",
      forgotPassword: "पासवर्ड भूल गए?",
      phoneCannotChange: "फ़ोन नंबर बदला नहीं जा सकता",
      emailNotVerified: "ईमेल सत्यापित नहीं है — सत्यापित करने के लिए \"OTP भेजें\" पर क्लिक करें",
      
      // Help
      helpMeApply: "आवेदन में मदद",
      needHelp: "मदद चाहिए?",
      contactUs: "हमसे संपर्क करें",
      faq: "अक्सर पूछे जाने वाले प्रश्न",
      
      // Forgot Password
      resetPassword: "पासवर्ड रीसेट करें",
      forgotPasswordDesc: "अपना पंजीकृत फोन नंबर दर्ज करें। आपके पंजीकृत ईमेल पर पासवर्ड रीसेट लिंक भेजा जाएगा।",
      sendResetLink: "रीसेट लिंक भेजें",
      sending: "भेज रहे हैं...",
      resetLinkSent: "रीसेट लिंक भेजा गया!",
      resetLinkSentDesc: "आपके पंजीकृत ईमेल पर पासवर्ड रीसेट लिंक भेजा गया है:",
      linkExpiresIn1Hour: "लिंक 1 घंटे में समाप्त हो जाएगा।",
      requestReceived: "अनुरोध प्राप्त हुआ",
      resetLinkGenericMsg: "यदि इस फोन नंबर से कोई खाता है और सत्यापित ईमेल है, तो रीसेट लिंक भेजा गया है।",
      backToLogin: "लॉगिन पर वापस जाएं",
      enterValidPhone: "कृपया वैध 10 अंकों का फोन नंबर दर्ज करें",
      
      // Profile Page
      changePassword: "पासवर्ड बदलें",
      currentPassword: "वर्तमान पासवर्ड",
      newPassword: "नया पासवर्ड",
      confirmNewPassword: "नया पासवर्ड पुष्टि करें",
      updatePassword: "पासवर्ड अपडेट करें",
      updatingPassword: "अपडेट हो रहा है...",
      emailAddress: "ईमेल पता",
      emailVerified: "ईमेल सत्यापित",
      sendOtp: "OTP भेजें",
      verifyOtp: "OTP सत्यापित करें",
      resendOtp: "OTP दोबारा भेजें",
      enterOtp: "6 अंकों का OTP दर्ज करें",
      notProvided: "उपलब्ध नहीं",
      saving: "सहेज रहे हैं...",

      // Government Jobs
      governmentJobs: "नौकरियां",
      searchJobsPlaceholder: "शीर्षक, संगठन, स्थान से नौकरी खोजें...",
      foundJobs: "पाई गईं",
      govJobs: "सरकारी नौकरियां",
      sortLatest: "अपेक्षित",
      sortExpected: "अपेक्षित",
      sortActive: "सक्रिय",
      sortDeadline: "सक्रिय",
      sortVacancies: "रिक्तियां",
      location: "स्थान",
      allIndia: "पूरे भारत",
      andhraPradesh: "आंध्र प्रदेश",
      telangana: "तेलंगाना",
      karnataka: "कर्नाटक",
      noJobsFound: "कोई नौकरी नहीं मिली",
      tryAdjustingJobs: "अपनी खोज या फिल्टर समायोजित करें",
      clearAllJobFilters: "सभी फिल्टर हटाएं",
      verified: "सत्यापित",
      expected: "अपेक्षित",
      rolling: "चालू",
      daysLeft: "दिन शेष",
      expired: "समाप्त",
      posts: "पद",
      viewOfficialNotification: "आधिकारिक अधिसूचना देखें",
      saveJob: "नौकरी सहेजें",
      share: "साझा करें",
      organisation: "संगठन",
      qualification: "योग्यता",
      vacancies: "रिक्तियां",
      salary: "वेतन",
      ageLimit: "आयु सीमा",
      source: "स्रोत",
      years: "वर्ष",
      daysRemaining: "दिन शेष",
      lastDate: "अंतिम तिथि",
      rollingApplication: "चालू आवेदन",
      expectedRecruitment: "अपेक्षित भर्ती",
      expectedRecruitmentDesc: "यह वार्षिक भर्ती कैलेंडर पर आधारित है। तिथियां और रिक्तियां सांकेतिक हैं। पुष्टि अधिसूचनाओं के लिए आधिकारिक वेबसाइट देखें।",
      recommendedTechJobs: "अनुशंसित तकनीकी नौकरियां",
      basedOnProfile: "आपकी IT/प्रौद्योगिकी प्रोफाइल के आधार पर",
      viewAll: "सभी देखें",
      savedGovJobs: "सहेजी गई सरकारी नौकरियां",
      noSavedJobsYet: "अभी तक कोई नौकरी सहेजी नहीं गई",
      saveJobsHint: "यहां देखने के लिए स्कीम्स टैब से सरकारी नौकरियां सहेजें",
      jobsDisclaimer: "KnowMySchemes सूचनात्मक उद्देश्यों के लिए आधिकारिक स्रोतों से सरकारी नौकरी अधिसूचनाएं एकत्र करता है। हम भर्ती एजेंसी नहीं हैं। आवेदन करने से पहले आधिकारिक वेबसाइट पर विवरण सत्यापित करें।",
      disclaimer: "अस्वीकरण",
    }
  },

  // =====================
  // ಕನ್ನಡ (KANNADA) - Native Translations
  // =====================
  kn: {
    translation: {
      // App Branding
      appName: "ಸರ್ಕಾರಿ ಯೋಜನೆಗಳು",
      tagline: "ನಾಗರಿಕ ಸಬಲೀಕರಣ",
      
      // Common Actions
      login: "ಲಾಗಿನ್",
      logout: "ಲಾಗ್‌ಔಟ್",
      signup: "ನೋಂದಾಯಿಸಿ",
      home: "ಮುಖಪುಟ",
      back: "ಹಿಂದೆ",
      search: "ಹುಡುಕಿ",
      filters: "ಫಿಲ್ಟರ್‌ಗಳು",
      clear: "ತೆಗೆದುಹಾಕಿ",
      viewDetails: "ವಿವರಗಳನ್ನು ನೋಡಿ",
      applyNow: "ಈಗ ಅರ್ಜಿ ಸಲ್ಲಿಸಿ",
      saved: "ಉಳಿಸಿದವು",
      all: "ಎಲ್ಲಾ",
      news: "ಸುದ್ದಿ",
      loading: "ಲೋಡ್ ಆಗುತ್ತಿದೆ...",
      submit: "ಸಲ್ಲಿಸಿ",
      cancel: "ರದ್ದುಮಾಡಿ",
      save: "ಉಳಿಸಿ",
      delete: "ಅಳಿಸಿ",
      edit: "ಬದಲಾಯಿಸಿ",
      close: "ಮುಚ್ಚಿ",
      yes: "ಹೌದು",
      no: "ಇಲ್ಲ",
      ok: "ಸರಿ",
      continue: "ಮುಂದುವರಿಸಿ",
      note: "ಸೂಚನೆ",
      adminDashboard: "ಅಡ್ಮಿನ್ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
      
      // Login Page
      welcomeBack: "ಮತ್ತೆ ಸ್ವಾಗತ",
      loginToAccess: "ಸರ್ಕಾರಿ ಯೋಜನೆಗಳನ್ನು ನೋಡಲು ಲಾಗಿನ್ ಮಾಡಿ",
      phoneNumber: "ಮೊಬೈಲ್ ಸಂಖ್ಯೆ",
      phonePlaceholder: "10 ಅಂಕಿಯ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ",
      password: "ಪಾಸ್‌ವರ್ಡ್",
      passwordPlaceholder: "ನಿಮ್ಮ ಪಾಸ್‌ವರ್ಡ್ ನಮೂದಿಸಿ",
      rememberMe: "ನನ್ನನ್ನು ನೆನಪಿಡಿ",
      loggingIn: "ಲಾಗಿನ್ ಆಗುತ್ತಿದೆ...",
      noAccount: "ಖಾತೆ ಇಲ್ಲವೇ?",
      forgotPassword: "ಪಾಸ್‌ವರ್ಡ್ ಮರೆತಿರಾ?",
      
      // Signup Page
      createAccount: "ಹೊಸ ಖಾತೆ ರಚಿಸಿ",
      signupTagline: "ನಿಮಗೆ ಸೂಕ್ತವಾದ ಯೋಜನೆಗಳನ್ನು ಹುಡುಕಿ",
      fullName: "ಪೂರ್ಣ ಹೆಸರು",
      fullNamePlaceholder: "ನಿಮ್ಮ ಪೂರ್ಣ ಹೆಸರು ನಮೂದಿಸಿ",
      confirmPassword: "ಪಾಸ್‌ವರ್ಡ್ ದೃಢೀಕರಿಸಿ",
      confirmPasswordPlaceholder: "ಪಾಸ್‌ವರ್ಡ್ ಮತ್ತೆ ನಮೂದಿಸಿ",
      state: "ರಾಜ್ಯ",
      selectState: "ರಾಜ್ಯವನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      district: "ಜಿಲ್ಲೆ",
      selectDistrict: "ಜಿಲ್ಲೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      age: "ವಯಸ್ಸು",
      agePlaceholder: "ನಿಮ್ಮ ವಯಸ್ಸು ನಮೂದಿಸಿ",
      selectAge: "ನಿಮ್ಮ ವಯಸ್ಸನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      casteCategory: "ಜಾತಿ ವರ್ಗ",
      selectCategory: "ವರ್ಗವನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      occupation: "ವೃತ್ತಿ",
      selectOccupation: "ವೃತ್ತಿಯನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      specifyOccupation: "ನಿಮ್ಮ ವೃತ್ತಿಯನ್ನು ತಿಳಿಸಿ",
      enterOccupation: "ವೃತ್ತಿ ನಮೂದಿಸಿ",
      gender: "ಲಿಂಗ",
      selectGender: "ಲಿಂಗವನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      genderMale: "ಪುರುಷ",
      genderFemale: "ಮಹಿಳೆ",
      genderOther: "ಇತರೆ",
      householdIncome: "ಕುಟುಂಬದ ವಾರ್ಷಿಕ ಆದಾಯ",
      selectIncome: "ಆದಾಯ ಶ್ರೇಣಿಯನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      creatingAccount: "ಖಾತೆ ರಚಿಸಲಾಗುತ್ತಿದೆ...",
      alreadyHaveAccount: "ಈಗಾಗಲೇ ಖಾತೆ ಇದೆಯೇ?",
      signupCaution: "ದಯವಿಟ್ಟು ನಿಖರವಾದ ವೈಯಕ್ತಿಕ ಮತ್ತು ಆದಾಯ ವಿವರಗಳನ್ನು ನಮೂದಿಸಿ. ತಪ್ಪು ಮಾಹಿತಿಯು ನಿಮ್ಮ ಅರ್ಹತೆಯ ಮೇಲೆ ಪರಿಣಾಮ ಬೀರಬಹುದು.",
      termsAgreementLabel: "ನಾನು ಓದಿದ್ದೇನೆ ಮತ್ತು ಒಪ್ಪುತ್ತೇನೆ",
      termsConditionsPrivacy: "ನಿಯಮಗಳು, ಷರತ್ತುಗಳು ಮತ್ತು ಗೌಪ್ಯತಾ ನೀತಿ",
      termsNotAccepted: "ನೋಂದಣಿಗೆ ನೀವು ನಿಯಮಗಳು, ಷರತ್ತುಗಳು ಮತ್ತು ಗೌಪ್ಯತಾ ನೀತಿಯನ್ನು ಒಪ್ಪಬೇಕು",
      termsAcceptedConfirm: "ನೀವು ನಿಯಮಗಳನ್ನು ಒಪ್ಪಿದ್ದೀರಿ",
      termsModalTitle: "ನಿಯಮಗಳು, ಷರತ್ತುಗಳು & ಗೌಪ್ಯತಾ ನೀತಿ",
      acceptTerms: "ನಾನು ನಿಯಮಗಳನ್ನು ಒಪ್ಪುತ್ತೇನೆ",
      
      // Occupation Options
      occupationAgriculture: "ಕೃಷಿ/ರೈತ",
      occupationIT: "ಸಾಫ್ಟ್‌ವೇರ್/ತಂತ್ರಜ್ಞಾನ",
      occupationBanking: "ಬ್ಯಾಂಕಿಂಗ್/ಹಣಕಾಸು ಕ್ಷೇತ್ರ",
      occupationBusiness: "ವ್ಯಾಪಾರ/ಸ್ವಯಂ ಉದ್ಯೋಗ",
      occupationAccountant: "ಲೆಕ್ಕಿಗ",
      occupationHomeMaker: "ಗೃಹಿಣಿ",
      occupationGovernment: "ಸರ್ಕಾರಿ ನೌಕರ",
      occupationHealthCare: "ಆರೋಗ್ಯ ಸೇವೆ",
      occupationCrafts: "ಕೈಕಸುಬು/ಕುಶಲಕರ್ಮಿ",
      occupationManualLabor: "ದೈನಿಕ ಕೂಲಿ ಕೆಲಸಗಾರ",
      occupationCreativeMedia: "ಕಲೆ/ಮಾಧ್ಯಮ",
      occupationStudent: "ವಿದ್ಯಾರ್ಥಿ",
      occupationSalesRetail: "ಮಾರಾಟ/ಚಿಲ್ಲರೆ",
      occupationTeachers: "ಶಿಕ್ಷಕ/ಪ್ರಾಧ್ಯಾಪಕ",
      occupationOthers: "ಇತರೆ ವೃತ್ತಿ",
      
      // Home Page
      indiaLargestPortal: "ಭಾರತದ ಅತಿದೊಡ್ಡ ಕಲ್ಯಾಣ ಯೋಜನೆಗಳ ವೆಬ್‌ಸೈಟ್",
      transformingLives: "ಜೀವನಗಳನ್ನು ಬದಲಾಯಿಸುತ್ತಿದೆ",
      incredibleIndia: "ಅದ್ಭುತ ಭಾರತ",
      reachingHouseholds: "ತಲುಪುತ್ತಿದೆ",
      householdsText: "30 ಕೋಟಿಗೂ ಹೆಚ್ಚು ಕುಟುಂಬಗಳಿಗೆ",
      acrossIndia: "ದೇಶಾದ್ಯಂತ ಕಲ್ಯಾಣ ಯೋಜನೆಗಳೊಂದಿಗೆ",
      discoverBenefits: "ನಿಮಗೆ ಸಿಗಬೇಕಾದ",
      youDeserve: "ಲಾಭಗಳನ್ನು ತಿಳಿಯಿರಿ",
      findBenefitsNow: "ನಿಮ್ಮ ಲಾಭಗಳನ್ನು ಈಗ ಹುಡುಕಿ",
      totalPopulation: "ಒಟ್ಟು ಜನಸಂಖ್ಯೆ",
      nationalCoverage: "ರಾಷ್ಟ್ರೀಯ ವ್ಯಾಪ್ತಿ",
      householdsReached: "ಲಾಭ ಪಡೆದ ಕುಟುಂಬಗಳು",
      directBenefitTransfers: "ನೇರ ವರ್ಗಾವಣೆಗಳು",
      realPeopleRealImpact: "ನಿಜವಾದ ಪರಿಣಾಮ",
      meetTheFaces: "ಫಲಾನುಭವಿಗಳನ್ನು ಭೇಟಿಮಾಡಿ",
      farmers: "ರೈತರು",
      women: "ಮಹಿಳೆಯರು",
      students: "ವಿದ್ಯಾರ್ಥಿಗಳು",
      seniors: "ಹಿರಿಯ ನಾಗರಿಕರು",
      everyMinuteCounts: "ಪ್ರತಿ ನಿಮಿಷವೂ ಮುಖ್ಯ",
      transferredPerMin: "ಪ್ರತಿ ನಿಮಿಷಕ್ಕೆ ವರ್ಗಾವಣೆ",
      familiesPerMin: "ಪ್ರತಿ ನಿಮಿಷಕ್ಕೆ ಕುಟುಂಬಗಳು",
      support247: "24 ಗಂಟೆ ಬೆಂಬಲ",
      dontMissOut: "ನಿಮ್ಮ ಲಾಭಗಳನ್ನು ಕಳೆದುಕೊಳ್ಳಬೇಡಿ",
      joinMillions: "ಲಕ್ಷಾಂತರ ಜನರಂತೆ ಜೀವನ ಬದಲಿಸುವ ಯೋಜನೆಗಳನ್ನು ಹುಡುಕಿ",
      startExploringNow: "ಈಗಲೇ ಅನ್ವೇಷಿಸಿ",
      exploreSchemes: "ಯೋಜನೆಗಳು & ಉದ್ಯೋಗಗಳು",
      navImpact: "KMS Smart",
      loginForImpact: "KMS Smart ಬಳಸಲು ಲಾಗಿನ್ ಮಾಡಿ",
      
      // Schemes Page
      discoverYourBenefits: "ನಿಮ್ಮ ಲಾಭಗಳನ್ನು ಹುಡುಕಿ",
      searchThrough: "ಹುಡುಕಿ",
      activeSchemes: "ಸಕ್ರಿಯ ಯೋಜನೆಗಳು",
      searchSchemes: "ಯೋಜನೆಗಳನ್ನು ಹುಡುಕಿ...",
      sector: "ಕ್ಷೇತ್ರ",
      selectSector: "ಕ್ಷೇತ್ರವನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      category: "ವರ್ಗ",
      type: "ಪ್ರಕಾರ",
      selectType: "ಪ್ರಕಾರವನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      found: "ಕಂಡುಬಂದವು",
      schemes: "ಯೋಜನೆಗಳು",
      scheme: "ಯೋಜನೆ",
      freeSchemes: "ಉಚಿತ ಯೋಜನೆಗಳು",
      premiumSchemes: "ಪ್ರೀಮಿಯಂ ಯೋಜನೆಗಳು",
      locked: "ಲಾಕ್ ಆಗಿದೆ",
      unlocked: "ಅನ್‌ಲಾಕ್ ಆಗಿದೆ",
      loadingSchemes: "ಯೋಜನೆಗಳು ಲೋಡ್ ಆಗುತ್ತಿವೆ...",
      noSchemesFound: "ಯೋಜನೆಗಳು ಸಿಗಲಿಲ್ಲ",
      tryAdjusting: "ಫಿಲ್ಟರ್‌ಗಳನ್ನು ಬದಲಿಸಿ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ",
      clearAllFilters: "ಎಲ್ಲಾ ಫಿಲ್ಟರ್‌ಗಳನ್ನು ತೆಗೆದುಹಾಕಿ",
      getWhatYouDeserve: "ನಿಮ್ಮ ಹಕ್ಕು ಪಡೆಯಿರಿ!",
      dontMissOutOnBenefits: "ಸರ್ಕಾರಿ ಲಾಭಗಳನ್ನು ಕಳೆದುಕೊಳ್ಳಬೇಡಿ. ನಿಮಗೆ ಅರ್ಹವಾದ ಯೋಜನೆಗಳನ್ನು ಹುಡುಕಲು ಮತ್ತು ಅರ್ಜಿ ಸಲ್ಲಿಸಲು ನಾವು ಸಹಾಯ ಮಾಡುತ್ತೇವೆ.",
      statesCovered: "ರಾಜ್ಯಗಳು",
      newSchemesAdded: "ಹೊಸ ಯೋಜನೆಗಳು ಸೇರಿಸಲಾಗಿದೆ!",
      farmerSchemes: "ರೈತ ಯೋಜನೆಗಳು",
      educationScholarships: "ಶಿಕ್ಷಣ ಮತ್ತು ವಿದ್ಯಾರ್ಥಿವೇತನ",
      womenEmpowerment: "ಮಹಿಳಾ ಸಬಲೀಕರಣ",
      healthWelfare: "ಆರೋಗ್ಯ ಮತ್ತು ಕಲ್ಯಾಣ",
      free: "ಉಚಿತ",
      premium: "ಪ್ರೀಮಿಯಂ",
      premiumScheme: "ಪ್ರೀಮಿಯಂ ಯೋಜನೆ",
      premiumSchemeAccessDesc: "ಇದು ಪ್ರೀಮಿಯಂ ಯೋಜನೆ. ನಿಮ್ಮ ಅರ್ಜಿಗೆ ತಜ್ಞರ ಸಹಾಯ ಪಡೆಯಲು ಚಂದಾದಾರರಾಗಿ.",
      unlockNow: "ಈಗ ಅನ್‌ಲಾಕ್ ಮಾಡಿ",
      getAssistance: "ಸಹಾಯ ಪಡೆಯಿರಿ",
      eligible: "ಅರ್ಹರು",
      youAreEligibleFor: "ನೀವು ಅರ್ಹರು",
      
      // Scheme Details
      eligibilityCriteria: "ಅರ್ಹತಾ ನಿಯಮಗಳು",
      benefits: "ಲಾಭಗಳು",
      requiredDocuments: "ಅಗತ್ಯ ದಾಖಲೆಗಳು",
      howToApply: "ಅರ್ಜಿ ಸಲ್ಲಿಸುವ ವಿಧಾನ",
      deadline: "ಕೊನೆಯ ದಿನಾಂಕ",
      browseMoreSchemes: "ಇನ್ನಷ್ಟು ಯೋಜನೆಗಳನ್ನು ನೋಡಿ",
      casteEligibility: "ಜಾತಿ ಅರ್ಹತೆ",
      schemeNotFound: "ಯೋಜನೆ ಸಿಗಲಿಲ್ಲ",
      schemeNotFoundDesc: "ಈ ಯೋಜನೆ ಲಭ್ಯವಿಲ್ಲ",
      backToSchemes: "ಯೋಜನೆಗಳಿಗೆ ಹಿಂತಿರುಗಿ",
      schemeDetails: "ಯೋಜನೆ ವಿವರಗಳು",
      
      // Saved Schemes
      yourSavedSchemes: "ಉಳಿಸಿದ ಯೋಜನೆಗಳು",
      youHave: "ನಿಮ್ಮ ಬಳಿ",
      savedSchemes: "ಉಳಿಸಿದ ಯೋಜನೆಗಳಿವೆ",
      savedScheme: "ಉಳಿಸಿದ ಯೋಜನೆ ಇದೆ",
      noSavedSchemesYet: "ಇನ್ನೂ ಯಾವುದೇ ಯೋಜನೆ ಉಳಿಸಿಲ್ಲ",
      startExploring: "ಯೋಜನೆಗಳನ್ನು ನೋಡಿ ಇಷ್ಟವಾದವುಗಳನ್ನು ಉಳಿಸಿ",
      browseAllSchemes: "ಎಲ್ಲಾ ಯೋಜನೆಗಳನ್ನು ನೋಡಿ",
      savedOn: "ಉಳಿಸಿದ ದಿನಾಂಕ",
      removeFromSaved: "ತೆಗೆದುಹಾಕಿ",
      allSchemes: "ಎಲ್ಲಾ ಯೋಜನೆಗಳು",
      
      // Notifications
      notifications: "ಅಧಿಸೂಚನೆಗಳು",
      markAllRead: "ಎಲ್ಲವನ್ನೂ ಓದಿದ್ದೆಂದು ಗುರುತಿಸಿ",
      noNotificationsYet: "ಅಧಿಸೂಚನೆಗಳು ಇಲ್ಲ",
      notifyAboutSchemes: "ಹೊಸ ಯೋಜನೆಗಳ ಬಗ್ಗೆ ತಿಳಿಸಲಾಗುವುದು",
      justNow: "ಈಗತಾನೆ",
      minutesAgo: "{{count}} ನಿಮಿಷಗಳ ಹಿಂದೆ",
      hoursAgo: "{{count}} ಗಂಟೆಗಳ ಹಿಂದೆ",
      daysAgo: "{{count}} ದಿನಗಳ ಹಿಂದೆ",
      
      // Premium/Subscription
      premiumAssistance: "ಪ್ರೀಮಿಯಂ ಸಹಾಯ",
      premiumAssistanceRequired: "ಪ್ರೀಮಿಯಂ ಸಹಾಯ ಅಗತ್ಯ",
      unlockPremium: "ಪ್ರೀಮಿಯಂ ಅನ್‌ಲಾಕ್ ಮಾಡಿ",
      subscribeNow: "ಈಗ ಚಂದಾದಾರರಾಗಿ",
      viewPlans: "ಪ್ಲಾನ್‌ಗಳನ್ನು ನೋಡಿ",
      maybeLater: "ನಂತರ ನೋಡುತ್ತೇನೆ",
      viewFreeSchemes: "ಉಚಿತ ಯೋಜನೆಗಳನ್ನು ನೋಡಿ",
      teamWillAssist: "ನಮ್ಮ ತಂಡ ಸಹಾಯ ಮಾಡುತ್ತದೆ",
      ourTeamWillContact: "ನಮ್ಮ ತಂಡ ನಿಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸುತ್ತದೆ",
      unlockHighValueSchemes: "ಹೆಚ್ಚಿನ ಮೌಲ್ಯದ ಯೋಜನೆಗಳನ್ನು ಅನ್‌ಲಾಕ್ ಮಾಡಿ",
      expertAssistanceDesc: "ಅರ್ಹತಾ ಪರಿಶೀಲನೆ, ಫಾರ್ಮ್ ತುಂಬುವಿಕೆ ಮತ್ತು ಸಂಪೂರ್ಣ ಅರ್ಜಿ ಸಹಾಯದಲ್ಲಿ ನಮ್ಮ ತಜ್ಞರು ನಿಮಗೆ ಸಹಾಯ ಮಾಡುತ್ತಾರೆ.",
      eligibilityVerification: "ಎಲ್ಲಾ ಯೋಜನೆಗಳಿಗೆ ಅರ್ಹತಾ ಪರಿಶೀಲನೆ",
      formFillingAssistance: "ಫಾರ್ಮ್ ತುಂಬುವಿಕೆ ಮತ್ತು ದಾಖಲೆ ಸಹಾಯ",
      teamContactsAfterPayment: "ಪಾವತಿಯ ನಂತರ ತಂಡ ನಿಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸುತ್ತದೆ",
      startingFrom: "ಆರಂಭಿಕ ಬೆಲೆ",
      perYear: "/ವರ್ಷಕ್ಕೆ",
      assistanceNote: "ನಾವು ಕೇವಲ ಸಹಾಯ ಸೇವೆಗಳನ್ನು ಒದಗಿಸುತ್ತೇವೆ. ಚಂದಾದಾರಿಕೆ ಯೋಜನೆ ಅನುಮೋದನೆಯ ಖಾತರಿ ನೀಡುವುದಿಲ್ಲ.",
      paymentSuccessful: "ಪಾವತಿ ಯಶಸ್ವಿಯಾಗಿದೆ! 🎉",
      teamWillContactSoon: "ನಿಮ್ಮ ಅರ್ಹ ಯೋಜನೆಗಳಲ್ಲಿ ಸಹಾಯ ಮಾಡಲು ನಮ್ಮ ತಂಡ ಶೀಘ್ರದಲ್ಲೇ ನಿಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸುತ್ತದೆ.",
      whatHappensNext: "ಮುಂದೆ ಏನಾಗುತ್ತದೆ:",
      keepPhoneAvailable: "ದಯವಿಟ್ಟು ನಿಮ್ಮ ಫೋನ್ ಅನ್ನು ಲಭ್ಯವಾಗಿ ಇಟ್ಟುಕೊಳ್ಳಿ.",
      documentGuidance: "ದಾಖಲೆ ಅಪ್‌ಲೋಡ್ ಮಾರ್ಗದರ್ಶನ",
      applicationSupport: "ಅರ್ಜಿ ಬೆಂಬಲ",
      eligibilityCheck: "ಅರ್ಹತಾ ಪರಿಶೀಲನೆ",
      eligibilityCheckDesc: "ಎಲ್ಲಾ ಅನ್ವಯಿಸುವ ಸರ್ಕಾರಿ ಯೋಜನೆಗಳಿಗೆ ನಿಮ್ಮ ಅರ್ಹತೆಯನ್ನು ನಾವು ಪರಿಶೀಲಿಸುತ್ತೇವೆ",
      formFilling: "ಫಾರ್ಮ್ ತುಂಬುವಿಕೆ",
      formFillingDesc: "ಅರ್ಜಿ ಫಾರ್ಮ್‌ಗಳು ಮತ್ತು ದಾಖಲೆ ತಯಾರಿಕೆಯಲ್ಲಿ ತಜ್ಞರ ಸಹಾಯ",
      phoneSupport: "ಫೋನ್ ಬೆಂಬಲ",
      phoneSupportDesc: "ಸಂಪೂರ್ಣ ಪ್ರಕ್ರಿಯೆಯಲ್ಲಿ ನಿಮಗೆ ಮಾರ್ಗದರ್ಶನ ಮಾಡಲು ಮೀಸಲಾದ ಬೆಂಬಲ ತಂಡ",
      getExpertHelp: "ನಿಮ್ಮ ಅರ್ಜಿಗಳಿಗೆ ತಜ್ಞರ ಸಹಾಯ ಪಡೆಯಿರಿ",
      expertHelpDesc: "ಅರ್ಹತಾ ಪರಿಶೀಲನೆಯಿಂದ ದಾಖಲೆ ಸಲ್ಲಿಕೆಯವರೆಗೆ ಸಂಪೂರ್ಣ ಅರ್ಜಿ ಪ್ರಕ್ರಿಯೆಯಲ್ಲಿ ನಮ್ಮ ತಜ್ಞರ ತಂಡ ನಿಮಗೆ ಮಾರ್ಗದರ್ಶನ ಮಾಡುತ್ತದೆ.",
      activeSubscription: "ಸಕ್ರಿಯ ಚಂದಾದಾರಿಕೆ",
      bestValue: "ಅತ್ಯುತ್ತಮ ಮೌಲ್ಯ",
      selectPlan: "ಪ್ಲಾನ್ ಆಯ್ಕೆಮಾಡಿ",
      alreadySubscribed: "ಈಗಾಗಲೇ ಚಂದಾದಾರರಾಗಿದ್ದೀರಿ",
      importantInformation: "ಮುಖ್ಯ ಮಾಹಿತಿ",
      termsAndConditions: "ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು",
      importantDisclaimer: "ಮುಖ್ಯ ನಿರಾಕರಣೆ",
      disclaimerText: "ನಾವು ಸರ್ಕಾರಿ ಯೋಜನೆಗಳು ಅಥವಾ ಪ್ರಯೋಜನಗಳನ್ನು ಮಾರಾಟ ಮಾಡುವುದಿಲ್ಲ. ಚಂದಾದಾರಿಕೆ ಶುಲ್ಕ ಕೇವಲ ಸೇವಾ ಸಹಾಯಕ್ಕಾಗಿ.",
      serviceIncludes: "ಸೇವೆಯಲ್ಲಿ ಸೇರಿದೆ:",
      importantPoints: "ಮುಖ್ಯ ಅಂಶಗಳು:",
      subscriptionOptions: "ಚಂದಾದಾರಿಕೆ ಆಯ್ಕೆಗಳು:",
      refundPolicy: "ಮರುಪಾವತಿ ನೀತಿ:",
      refundPolicyText: "ಕಾನೂನಿನ ಪ್ರಕಾರ ಅಗತ್ಯವಿಲ್ಲದಿದ್ದರೆ ಯಶಸ್ವಿ ಪಾವತಿಯ ನಂತರ ಯಾವುದೇ ಮರುಪಾವತಿ ಇಲ್ಲ.",
      agreeAndPay: "ಒಪ್ಪಿ ಮತ್ತು ಪಾವತಿಸಿ",
      processing: "ಪ್ರೊಸೆಸ್ ಆಗುತ್ತಿದೆ...",
      
      // Filters
      central: "ಕೇಂದ್ರ",
      stateType: "ರಾಜ್ಯ",
      general: "ಸಾಮಾನ್ಯ",
      sc: "ಪರಿಶಿಷ್ಟ ಜಾತಿ",
      st: "ಪರಿಶಿಷ್ಟ ಪಂಗಡ",
      obc: "ಇತರೆ ಹಿಂದುಳಿದ ವರ್ಗ",
      
      // Sector Names
      sectorAgriculture: "ಕೃಷಿ",
      sectorHealth: "ಆರೋಗ್ಯ",
      sectorHousing: "ವಸತಿ",
      sectorEducation: "ಶಿಕ್ಷಣ",
      sectorEmployment: "ಉದ್ಯೋಗ",
      sectorBusiness: "ವ್ಯಾಪಾರ",
      sectorWomenChild: "ಮಹಿಳೆ ಮತ್ತು ಮಕ್ಕಳು",
      sectorSocialWelfare: "ಸಮಾಜ ಕಲ್ಯಾಣ",
      sectorFinancial: "ಹಣಕಾಸು ಸೇವೆಗಳು",
      sectorRural: "ಗ್ರಾಮೀಣ ಅಭಿವೃದ್ಧಿ",
      sectorUrban: "ನಗರ ಅಭಿವೃದ್ಧಿ",
      sectorSkill: "ಕೌಶಲ್ಯ ಅಭಿವೃದ್ಧಿ",
      sectorSkillsEmployment: "ಕೌಶಲ್ಯ ಮತ್ತು ಉದ್ಯೋಗ",
      sectorFood: "ಆಹಾರ ಭದ್ರತೆ",
      sectorInsurance: "ವಿಮೆ",
      sectorPension: "ಪಿಂಚಣಿ",
      
      // States
      andhrapradesh: "ಆಂಧ್ರಪ್ರದೇಶ",
      telangana: "ತೆಲಂಗಾಣ",
      karnataka: "ಕರ್ನಾಟಕ",
      tamilnadu: "ತಮಿಳುನಾಡು",
      kerala: "ಕೇರಳ",
      maharashtra: "ಮಹಾರಾಷ್ಟ್ರ",
      
      // Language
      language: "ಭಾಷೆ",
      english: "English",
      telugu: "తెలుగు",
      hindi: "हिंदी",
      kannada: "ಕನ್ನಡ",
      selectLanguage: "ಭಾಷೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      
      // News Page
      latestNews: "ಇತ್ತೀಚಿನ ಸುದ್ದಿ",
      latestIndiaNews: "ಭಾರತದ ಇತ್ತೀಚಿನ ಸುದ್ದಿ",
      newsUpdates: "ಸುದ್ದಿ ನವೀಕರಣಗಳು",
      readMore: "ಇನ್ನಷ್ಟು ಓದಿ",
      lastUpdated: "ಕೊನೆಯ ನವೀಕರಣ",
      refreshNews: "ಸುದ್ದಿ ರಿಫ್ರೆಶ್ ಮಾಡಿ",
      searchNews: "ಸುದ್ದಿ ಹುಡುಕಿ...",
      loadingNews: "ಸುದ್ದಿ ಲೋಡ್ ಆಗುತ್ತಿದೆ...",
      noNewsFound: "ಸುದ್ದಿ ಸಿಗಲಿಲ್ಲ",
      tryDifferentSearch: "ಬೇರೆ ಪದದಿಂದ ಹುಡುಕಿ",
      newsWillBeUpdated: "ಸುದ್ದಿ ಶೀಘ್ರದಲ್ಲೇ ನವೀಕರಿಸಲಾಗುವುದು",
      
      // Profile
      profile: "ಪ್ರೊಫೈಲ್",
      myProfile: "ನನ್ನ ಪ್ರೊಫೈಲ್",
      editProfile: "ಪ್ರೊಫೈಲ್ ಬದಲಾಯಿಸಿ",
      saveChanges: "ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ",
      deleteAccount: "ಖಾತೆ ಅಳಿಸಿ",
      personalInfo: "ವೈಯಕ್ತಿಕ ಮಾಹಿತಿ",
      accountSettings: "ಖಾತೆ ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
      
      // Errors & Messages
      error: "ದೋಷ",
      success: "ಯಶಸ್ವಿ",
      somethingWentWrong: "ಏನೋ ತಪ್ಪಾಗಿದೆ",
      tryAgain: "ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ",
      pleaseWait: "ದಯವಿಟ್ಟು ನಿರೀಕ್ಷಿಸಿ...",
      noDataFound: "ಮಾಹಿತಿ ಸಿಗಲಿಲ್ಲ",
      connectionError: "ಸಂಪರ್ಕ ದೋಷ",
      
      // Common UI
      back: "ಹಿಂದಕ್ಕೆ",
      home: "ಮುಖಪುಟ",
      cancel: "ರದ್ದುಮಾಡಿ",
      saveChanges: "ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ",
      editProfile: "ಪ್ರೊಫೈಲ್ ಸಂಪಾದಿಸಿ",
      profileInfo: "ಪ್ರೊಫೈಲ್ ಮಾಹಿತಿ",
      fullName: "ಪೂರ್ಣ ಹೆಸರು",
      phoneNumber: "ಫೋನ್ ಸಂಖ್ಯೆ",
      district: "ಜಿಲ್ಲೆ",
      age: "ವಯಸ್ಸು",
      occupation: "ಉದ್ಯೋಗ",
      caste: "ಜಾತಿ / ವರ್ಗ",
      annualIncome: "ವಾರ್ಷಿಕ ಮನೆ ಆದಾಯ",
      forgotPassword: "ಪಾಸ್‌ವರ್ಡ್ ಮರೆತಿರಾ?",
      phoneCannotChange: "ಫೋನ್ ಸಂಖ್ಯೆ ಬದಲಾಯಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ",
      emailNotVerified: "ಇಮೇಲ್ ಪರಿಶೀಲಿಸಲಾಗಿಲ್ಲ — ಪರಿಶೀಲಿಸಲು \"OTP ಕಳುಹಿಸಿ\" ಕ್ಲಿಕ್ ಮಾಡಿ",
      
      // Help
      helpMeApply: "ಅರ್ಜಿ ಸಲ್ಲಿಸಲು ಸಹಾಯ",
      needHelp: "ಸಹಾಯ ಬೇಕೇ?",
      contactUs: "ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ",
      faq: "ಪದೇ ಪದೇ ಕೇಳುವ ಪ್ರಶ್ನೆಗಳು",
      
      // Forgot Password
      resetPassword: "ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಿ",
      forgotPasswordDesc: "ನಿಮ್ಮ ನೋಂದಾಯಿತ ಫೋನ್ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ. ನಿಮ್ಮ ನೋಂದಾಯಿತ ಇಮೇಲ್‌ಗೆ ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸುವ ಲಿಂಕ್ ಕಳುಹಿಸಲಾಗುವುದು.",
      sendResetLink: "ಮರುಹೊಂದಿಸುವ ಲಿಂಕ್ ಕಳುಹಿಸಿ",
      sending: "ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ...",
      resetLinkSent: "ಮರುಹೊಂದಿಸುವ ಲಿಂಕ್ ಕಳುಹಿಸಲಾಗಿದೆ!",
      resetLinkSentDesc: "ನಿಮ್ಮ ನೋಂದಾಯಿತ ಇಮೇಲ್‌ಗೆ ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸುವ ಲಿಂಕ್ ಕಳುಹಿಸಲಾಗಿದೆ:",
      linkExpiresIn1Hour: "ಲಿಂಕ್ 1 ಗಂಟೆಯಲ್ಲಿ ಮುಕ್ತಾಯವಾಗುತ್ತದೆ.",
      requestReceived: "ವಿನಂತಿ ಸ್ವೀಕರಿಸಲಾಗಿದೆ",
      resetLinkGenericMsg: "ಈ ಫೋನ್ ಸಂಖ್ಯೆಯೊಂದಿಗೆ ಖಾತೆ ಇದ್ದರೆ ಮತ್ತು ಪರಿಶೀಲಿಸಿದ ಇಮೇಲ್ ಇದ್ದರೆ, ಮರುಹೊಂದಿಸುವ ಲಿಂಕ್ ಕಳುಹಿಸಲಾಗಿದೆ.",
      backToLogin: "ಲಾಗಿನ್‌ಗೆ ಹಿಂತಿರುಗಿ",
      enterValidPhone: "ಮಾನ್ಯ 10 ಅಂಕಿಯ ಫೋನ್ ಸಂಖ್ಯೆ ನಮೂದಿಸಿ",
      
      // Profile Page
      changePassword: "ಪಾಸ್‌ವರ್ಡ್ ಬದಲಾಯಿಸಿ",
      currentPassword: "ಪ್ರಸ್ತುತ ಪಾಸ್‌ವರ್ಡ್",
      newPassword: "ಹೊಸ ಪಾಸ್‌ವರ್ಡ್",
      confirmNewPassword: "ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ ದೃಢೀಕರಿಸಿ",
      updatePassword: "ಪಾಸ್‌ವರ್ಡ್ ನವೀಕರಿಸಿ",
      updatingPassword: "ನವೀಕರಿಸಲಾಗುತ್ತಿದೆ...",
      emailAddress: "ಇಮೇಲ್ ವಿಳಾಸ",
      emailVerified: "ಇಮೇಲ್ ಪರಿಶೀಲಿಸಲಾಗಿದೆ",
      sendOtp: "OTP ಕಳುಹಿಸಿ",
      verifyOtp: "OTP ಪರಿಶೀಲಿಸಿ",
      resendOtp: "OTP ಮರುಕಳುಹಿಸಿ",
      enterOtp: "6 ಅಂಕಿಯ OTP ನಮೂದಿಸಿ",
      notProvided: "ಒದಗಿಸಿಲ್ಲ",
      saving: "ಉಳಿಸಲಾಗುತ್ತಿದೆ...",

      // Government Jobs
      governmentJobs: "ಉದ್ಯೋಗಗಳು",
      searchJobsPlaceholder: "ಶೀರ್ಷಿಕೆ, ಸಂಸ್ಥೆ, ಸ್ಥಳದಿಂದ ಉದ್ಯೋಗ ಹುಡುಕಿ...",
      foundJobs: "ಕಂಡುಬಂದಿವೆ",
      govJobs: "ಸರ್ಕಾರಿ ಉದ್ಯೋಗಗಳು",
      sortLatest: "ನಿರೀಕ್ಷಿತ",
      sortExpected: "ನಿರೀಕ್ಷಿತ",
      sortActive: "ಸಕ್ರಿಯ",
      sortDeadline: "ಸಕ್ರಿಯ",
      sortVacancies: "ಖಾಲಿ ಹುದ್ದೆಗಳು",
      location: "ಸ್ಥಳ",
      allIndia: "ಅಖಿಲ ಭಾರತ",
      andhraPradesh: "ಆಂಧ್ರ ಪ್ರದೇಶ",
      telangana: "ತೆಲಂಗಾಣ",
      karnataka: "ಕರ್ನಾಟಕ",
      noJobsFound: "ಯಾವುದೇ ಉದ್ಯೋಗಗಳು ಕಂಡುಬಂದಿಲ್ಲ",
      tryAdjustingJobs: "ನಿಮ್ಮ ಹುಡುಕಾಟ ಅಥವಾ ಫಿಲ್ಟರ್ ಸರಿಹೊಂದಿಸಿ",
      clearAllJobFilters: "ಎಲ್ಲಾ ಫಿಲ್ಟರ್ ತೆಗೆಯಿರಿ",
      verified: "ಪರಿಶೀಲಿಸಲಾಗಿದೆ",
      expected: "ನಿರೀಕ್ಷಿತ",
      rolling: "ನಡೆಯುತ್ತಿದೆ",
      daysLeft: "ದಿನಗಳು ಬಾಕಿ",
      expired: "ಅವಧಿ ಮುಗಿದಿದೆ",
      posts: "ಹುದ್ದೆಗಳು",
      viewOfficialNotification: "ಅಧಿಕೃತ ಅಧಿಸೂಚನೆ ವೀಕ್ಷಿಸಿ",
      saveJob: "ಉದ್ಯೋಗ ಉಳಿಸಿ",
      share: "ಹಂಚಿಕೊಳ್ಳಿ",
      organisation: "ಸಂಸ್ಥೆ",
      qualification: "ಅರ್ಹತೆ",
      vacancies: "ಖಾಲಿ ಹುದ್ದೆಗಳು",
      salary: "ಸಂಬಳ",
      ageLimit: "ವಯಸ್ಸಿನ ಮಿತಿ",
      source: "ಮೂಲ",
      years: "ವರ್ಷಗಳು",
      daysRemaining: "ದಿನಗಳು ಬಾಕಿ",
      lastDate: "ಕೊನೆಯ ದಿನಾಂಕ",
      rollingApplication: "ನಿರಂತರ ಅರ್ಜಿ",
      expectedRecruitment: "ನಿರೀಕ್ಷಿತ ನೇಮಕಾತಿ",
      expectedRecruitmentDesc: "ಇದು ವಾರ್ಷಿಕ ನೇಮಕಾತಿ ಕ್ಯಾಲೆಂಡರ್ ಆಧಾರಿತ. ದಿನಾಂಕ ಮತ್ತು ಖಾಲಿ ಹುದ್ದೆಗಳು ಸೂಚಕ. ಅಧಿಕೃತ ವೆಬ್‌ಸೈಟ್‌ನಲ್ಲಿ ಪರಿಶೀಲಿಸಿ.",
      recommendedTechJobs: "ಶಿಫಾರಸು ಮಾಡಿದ ತಂತ್ರಜ್ಞಾನ ಉದ್ಯೋಗಗಳು",
      basedOnProfile: "ನಿಮ್ಮ IT/ತಂತ್ರಜ್ಞಾನ ಪ್ರೊಫೈಲ್ ಆಧಾರದ ಮೇಲೆ",
      viewAll: "ಎಲ್ಲಾ ವೀಕ್ಷಿಸಿ",
      savedGovJobs: "ಉಳಿಸಿದ ಸರ್ಕಾರಿ ಉದ್ಯೋಗಗಳು",
      noSavedJobsYet: "ಇನ್ನೂ ಯಾವುದೇ ಉದ್ಯೋಗ ಉಳಿಸಿಲ್ಲ",
      saveJobsHint: "ಇಲ್ಲಿ ನೋಡಲು ಯೋಜನೆಗಳ ಟ್ಯಾಬ್‌ನಿಂದ ಸರ್ಕಾರಿ ಉದ್ಯೋಗಗಳನ್ನು ಉಳಿಸಿ",
      jobsDisclaimer: "KnowMySchemes ಮಾಹಿತಿ ಉದ್ದೇಶಗಳಿಗಾಗಿ ಅಧಿಕೃತ ಮೂಲಗಳಿಂದ ಸರ್ಕಾರಿ ಉದ್ಯೋಗ ಅಧಿಸೂಚನೆಗಳನ್ನು ಸಂಗ್ರಹಿಸುತ್ತದೆ. ನಾವು ನೇಮಕಾತಿ ಸಂಸ್ಥೆ ಅಲ್ಲ. ಅರ್ಜಿ ಸಲ್ಲಿಸುವ ಮೊದಲು ಅಧಿಕೃತ ವೆಬ್‌ಸೈಟ್‌ನಲ್ಲಿ ವಿವರಗಳನ್ನು ಪರಿಶೀಲಿಸಿ.",
      disclaimer: "ಹಕ್ಕು ನಿರಾಕರಣೆ",
    }
  }
};

// i18n configuration
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'te', 'hi', 'kn'],
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'preferredLanguage',
      caches: ['localStorage'],
    },
    
    interpolation: {
      escapeValue: false,
    },
    
    react: {
      useSuspense: false,
    },
  });

export default i18n;
